import React, { useState, useEffect, useRef, useCallback } from "react";
import { scaleLinear } from "@visx/scale";
import { Group } from "@visx/group";
import { GridRows, GridColumns } from "@visx/grid";
import { makeInactiveOnCondition } from "./inactiveOnCondition";
import { findClosestNote } from "@/lib/key";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import autotuneIntroductionVideoThumbnail from "../../../public/images/youtubeThumbnails/hans/autotune-introduction-video.jpg";
import { Text } from "@visx/text";
import * as Tone from "tone";
import { isHotkeyPressed, useHotkeys } from "react-hotkeys-hook";
import * as mathjs from "mathjs";
import { LinePath, Circle, Line } from "@visx/shape";
import { curveBasis } from "@visx/curve";
import { Direction, Range, getTrackBackground } from "react-range";
import { ScaleLinear } from "d3-scale";
import lodash from "lodash";
import {
  PlusCircleIcon,
  MinusCircleIcon,
  PlayCircleIcon,
  PauseCircleIcon,
  StopCircleIcon,
  XCircleIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  QuestionMarkCircleIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
} from "@heroicons/react/24/outline";
import { Dialog, RadioGroup } from "@headlessui/react";
import { classNames } from "@/utils/classNames";
import { SmallTitle } from "../text/SmallTitle";
import { Tooltip } from "react-tooltip";
import { useNamedPitchCorrection } from "@/lib/zustand/usePitchCorrection";
import { Undoable } from "@/types/defs/pitchCorrection/undoable";
import { Marker } from "@/types/defs/pitchCorrection/marker";
import { frequencyToSemitones } from "@/utils/frequencyToSemitones";
import { NormalText } from "../text/NormalText";
import Image from "next/image";
import knobsPic from "../../../public/images/knobs.png";
import { usePostHog } from "posthog-js/react";
import { VibratoKnob } from "./VibratoKnob";
import { smoothAtKinks } from "./smoothAtKinks";
import { PitchKnob } from "./PitchKnob";
import { calculatePitchesForPitchChange } from "@/components/pitchCorrection/pitchKnob/calculatePitchesForPitchChange";
import { getSegmentationPoints2 } from "@/components/pitchCorrection/getSegmentationPoints2";
import { keyStore } from "@/components/pitchCorrection/keyStore";
import {
  anchorBleed,
  anchorInner,
} from "@/components/pitchCorrection/constants";
import { PitchSelectMenu } from "./PitchSelectMenu";
import YouTube, { YouTubeProps } from "react-youtube";
import { useNamedSelectedArtist } from "@/lib/zustand/useSelectedArtist";
import { makeDiatonicTranspose } from "@/components/pitchCorrection/makeDiatonicTranspose";
import { runner_ } from "./runner";
import {
  InFlightRender,
  SegmentRenderContext,
} from "@/types/defs/pitchCorrection/segmentRender";
import { computeRedoTransition } from "@/components/pitchCorrection/undo/computeRedoTransition";
import { computeUndoTransition } from "@/components/pitchCorrection/undo/computeUndoTransition";
import {
  UndoTransition,
  UndoTransitionInputs,
} from "@/types/defs/pitchCorrection/undo";
import { useNamedKey } from "@/lib/zustand/useKey";
import {
  AI_BACKEND_URL_REDEEM_SESSION_SLUG,
  AI_BACKEND_URL_REDEEM_SESSION_SLUG_FOR_FREE,
  RUNNER_DEBOUNCE,
} from "@/constants/constants";
import { Button } from "@/components/buttons/Button";
// import LinkButton from "../links/LinkButton";
// import usePricingModal from "@/lib/zustand/usePricingModal";
// import SoftText from "../text/SoftText";

// Both live in plain modules so server code (the harmonies API) can use them without loading this component; re-exported here so existing importers keep working.
export { getSegmentationPoints2, keyStore };

const audimeePrimary = "#80578a";
const audimeeSecondary = "#e8deea";
const selectedColor = "#00a86b";
const rangeActiveBackground = audimeeSecondary;
const rangeDisabledBackground = "#e5e7eb";
const handleActiveBackground = audimeePrimary;
const handleDisabledBackground = audimeeSecondary;

const doNotDrawMe = -42;

type PitchIndexInfo = {
  ix: number;
  leftOuterPoint: number;
  rightOuterPoint: number;
};
type Mode = "pitch" | "drift" | "vibrato" | "cut";

const determineZoomSufficiency = (
  l: number,
  r: number,
  lo: number,
  hi: number,
  ad: number
) => ad > 0 && (r - l) * ad < 14 && hi - lo < 24;

const useReactiveSvg = (isFullScreen: boolean) => {
  const [svgSize, setSvgSize] = useState({ width: 800, height: 400 });

  useEffect(() => {
    const handleResize = () => {
      const { innerWidth: width, innerHeight: height } = window;
      const widthMax = 1280; // max-w-7xl
      const widthMaxLeft = 1030;
      const widthInflection = 1000;
      const widthMinLeft = 700;
      const widthLargeFactor = 0.9;
      const widthSmallFactor = 0.84;
      const widthMaxFactor = 0.91;
      const heightMax = 600;
      const heightMin = 500;
      const heightMaxFactor = 0.6;
      const heightMinFactor = 0.4;
      const heightFactor =
        height > heightMax ? heightMaxFactor
        : height < heightMin ? heightMinFactor
        : scaleLinear({
            domain: [heightMin, heightMax],
            range: [heightMinFactor, heightMaxFactor],
          })(height);
      const widthFactor =
        width > widthMaxLeft ? widthLargeFactor
        : width < widthMinLeft ? widthSmallFactor
        : width > widthInflection ?
          scaleLinear({
            range: [widthMaxFactor, widthLargeFactor],
            domain: [widthInflection, widthMaxLeft],
          })(width)
        : scaleLinear({
            range: [widthSmallFactor, widthMaxFactor],
            domain: [widthMinLeft, widthInflection],
          })(width);
      if (isFullScreen) {
        setSvgSize({ width: width - 90, height: height - 200 });
      } else {
        setSvgSize({
          width: Math.floor(Math.min(width, widthMax) * widthFactor),
          height: Math.floor(height * heightFactor),
        });
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize(); // Initial call to set the size based on the initial window size

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isFullScreen]);

  return svgSize;
};
const LoadingSpinner = ({ className }: { className: string }) => {
  return (
    <div className={classNames("animate-spin text-gray-500", className)}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="flex-1 animate-spin stroke-current stroke-[2]"
        fill="none"
        viewBox="0 0 24 24"
      >
        <path
          d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
          className="stroke-current opacity-25"
        />
        <path d="M12 2C6.47715 2 2 6.47715 2 12C2 14.7255 3.09032 17.1962 4.85857 19" />
      </svg>
    </div>
  );
};
const doPitchIndicesAbut = (
  pitchIndexL: PitchIndexInfo,
  pitchIndexR: PitchIndexInfo
) =>
  pitchIndexL.rightOuterPoint + 1 === pitchIndexR.leftOuterPoint ||
  pitchIndexL.rightOuterPoint === pitchIndexR.leftOuterPoint;

// Fire-and-forget close for a context nothing owns anymore; a repeated close rejects, which is fine — the leak is gone either way.
const closeAudioContextSilently = async (audioContext: AudioContext) => {
  try {
    await audioContext.close();
  } catch {
    // Already closed or closing.
  }
};

// Utility function to load the WAV file. The caller gets the context handed out the MOMENT it exists (before the decode await), so an unmount racing the load can still close it — a context that only reaches React state via setState after the awaits leaks for the document's life when the component is already gone, and Chrome caps a document at ~6 live AudioContexts.
const loadWavFile = async (
  url: string,
  onAudioContextCreated: (audioContext: AudioContext) => void
) => {
  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  const audioContext = new window.AudioContext();
  onAudioContextCreated(audioContext);
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  const duration = audioBuffer.duration;
  return { audioBuffer, duration, audioContext };
};

const normalize = (nums: number[]) => {
  const wvMin = mathjs.min(nums);
  const wvMax = mathjs.max(nums);
  return nums.map((x) => (x > 0 ? x / wvMax : (-1 * x) / wvMin));
};

const decimate = (data: number[], targetLengths: number[]) => {
  const results: number[][] = targetLengths.map(() => []);
  const length = data.length;

  // Initialize accumulators and counters for each target length
  const accumulators = targetLengths.map(() => 0);
  const maxs = targetLengths.map(() => 0);
  const mins = targetLengths.map(() => 10000000);
  const counts = targetLengths.map(() => 0);
  const chunkSizes = targetLengths.map((targetLength) =>
    Math.floor(length / targetLength)
  );

  for (let i = 0; i < length; i++) {
    for (let j = 0; j < targetLengths.length; j++) {
      accumulators[j] += data[i];
      maxs[j] = Math.max(maxs[j], data[i]);
      mins[j] = Math.min(mins[j], data[i]);
      counts[j]++;

      if (counts[j] === chunkSizes[j]) {
        // results[j].push(accumulators[j] / counts[j]);
        results[j].push(maxs[j], mins[j]);
        accumulators[j] = 0;
        maxs[j] = 0;
        mins[j] = 10000000;
        counts[j] = 0;
      }
    }
  }

  // Handle remaining elements if any
  for (let j = 0; j < targetLengths.length; j++) {
    if (counts[j] > 0) {
      // results[j].push(accumulators[j] / counts[j]);
      results[j].push(maxs[j], mins[j]);
    }
  }

  return results;
};

const margin = { top: 20, right: 0, bottom: 0, left: 40 };
const zoomFactorForWheelX = 0.015;
const baseYZoom = 0.3;
const amountToIncreaseYZoomPerOctave = 0.1;
const intervalAboveWhichWeNeedFasterZoomingInSemitones = 12;
const oneOctaveInSemitones = 12;
const zoomFactorForWheelY = ([b, t]: [number, number]) =>
  baseYZoom +
  (t - b < intervalAboveWhichWeNeedFasterZoomingInSemitones ?
    0
  : scaleLinear({
      domain: [
        intervalAboveWhichWeNeedFasterZoomingInSemitones,
        intervalAboveWhichWeNeedFasterZoomingInSemitones + oneOctaveInSemitones,
      ],
      range: [0.0, amountToIncreaseYZoomPerOctave],
    })(t - b));
const decimationMinimum = 1;
const topBarHeight = 12;
const pitchLabelOffset = -10;

const minXMarkerInterval = 0.25;

const getXMarkerInterval = (zoomLevel: number, audioDuration: number) => {
  const zlad = zoomLevel * audioDuration;
  // NaN never satisfies the loop condition below and would spin forever; fall back to the smallest interval (the zlad <= 2 result)
  if (!Number.isFinite(zlad)) return minXMarkerInterval;
  let i = 1;
  let j = -2;
  while (true) {
    if (Math.pow(2, i) >= zlad) return Math.pow(2, j);
    i++;
    j++;
  }
};

enum YMarkerZoom {
  CLOSE = 1,
  FAR,
}

const calcInnerPoints = (l: number, r: number) => ({
  leftInnerPoint: Math.min(l + anchorInner, Math.max(r - anchorInner, l + 1)),
  rightInnerPoint: Math.min(r - anchorInner, Math.max(l + anchorInner, r - 1)),
});

const calcBleedPoints = (l: number, r: number, e: number) => ({
  leftBleedPoint: Math.max(l - anchorBleed, 0),
  rightBleedPoint: Math.min(r + anchorBleed, e - 1),
});

const getYMarkerInterval = (zoomLevel: number) => {
  if (zoomLevel < 25) {
    return YMarkerZoom.CLOSE;
  } else {
    return YMarkerZoom.FAR;
  }
};

const generatePitchLabels = (
  minY: number,
  maxY: number,
  interval: YMarkerZoom
) => {
  const pitchLabels = [];
  for (let i = Math.ceil(minY); i <= Math.floor(maxY); i += 1) {
    const octave = Math.floor(i / 12);
    const noteIndex = ((i % 12) + 12) % 12;
    const note = [
      "C",
      "C#",
      "D",
      "D#",
      "E",
      "F",
      "F#",
      "G",
      "G#",
      "A",
      "A#",
      "B",
    ][noteIndex];
    if (
      (interval === YMarkerZoom.CLOSE &&
        (note === "C" ||
          note === "D" ||
          note === "E" ||
          note === "F" ||
          note === "G" ||
          note === "A" ||
          note === "B")) ||
      (interval === YMarkerZoom.FAR &&
        (note === "D" || note === "G" || note === "A"))
    )
      pitchLabels.push({ pitch: `${note}${octave}`, value: i });
  }
  return pitchLabels;
};
const renderPitchLines = (
  pitchData: number[],
  pitchIndices: PitchIndexInfo[],
  [pitchCullLSuggestion, pitchCullRSuggestion]: [number, number],
  inShift: boolean,
  xZoomScale: (x: number) => number,
  yZoomScale: (x: number) => number
) => {
  const segments = [];
  const leftPoints = lodash.uniq(
    lodash.map(pitchIndices, (pi) => pi.leftOuterPoint)
  );
  const rightPoints = lodash.uniq(
    lodash.map(pitchIndices, (pi) => pi.rightOuterPoint)
  );
  let segment: { x: number; y: number }[] = [];
  // we extend the cull in case there is a selection outside of the range
  // there are more scientific ways to do this...
  const pitchCullL = Math.floor(
    Math.max(
      0,
      pitchCullLSuggestion - (pitchCullRSuggestion - pitchCullLSuggestion) / 2
    )
  );
  const pitchCullR = Math.floor(
    Math.min(
      pitchData.length,
      pitchCullRSuggestion + (pitchCullRSuggestion - pitchCullLSuggestion) / 2
    )
  );
  pitchData.slice(pitchCullL, pitchCullR).forEach((pitch, index) => {
    const culledIndex = index + pitchCullL;
    const toPush = { x: culledIndex, y: pitch };
    if (lodash.includes(rightPoints, culledIndex) && segment.length > 0) {
      segment.push(toPush);
      segments.push({ segment, selected: true });
      segment = [toPush];
    } else if (lodash.includes(leftPoints, culledIndex) && segment.length > 0) {
      segment.push(toPush);
      segments.push({ segment, selected: false });
      segment = [toPush];
    } else if (pitch === 0 && segment.length > 0) {
      segments.push({ segment, selected: false });
      segment = [];
    } else if (pitch !== 0) {
      segment.push(toPush);
    }
  });

  if (segment.length > 0) {
    segments.push({ segment, selected: false });
  }

  return segments
    .filter((s) => s.segment.length > 1)
    .map(({ selected, segment: dataSegment }, index) => (
      <LinePath
        key={index}
        data={dataSegment}
        x={(d) => xZoomScale(d.x)}
        y={(d) => yZoomScale(d.y)}
        stroke={selected && !inShift ? selectedColor : "#ff7b00"}
        strokeWidth={1.5}
        curve={curveBasis}
      />
    ));
};

// as audimee is used mostly on pop music
// we want scoops to sound more scoopy
// this function is a bit of a hack to make that happen
// it's left-biased towards the most drastic pitch change
// so if someone is scooping up, it will shorten the scoop until
// it hits this change
export const makeCirclesSnappier = (
  circles: Marker[],
  pitches: number[],
  leftBias = 20
) => {
  const slopes = pitches.map((p, i) => (i === 0 ? 0 : p - pitches[i - 1]));

  const outLeft: Marker[] = [];

  for (let i = 0; i < circles.length; i++) {
    const circle = circles[i];
    if (circle.isEndpoint) {
      outLeft.push(circle);
    } else if (
      outLeft.length > 0 &&
      circle.index - outLeft[outLeft.length - 1].index > 30
    ) {
      outLeft.push(circle);
    } else {
      let cx = circle.index;
      let cs = Math.abs(slopes[cx]);
      for (
        let j = Math.max(
          outLeft.length > 0 ? outLeft[outLeft.length - 1].index + 5 : 0,
          circle.index - leftBias
        );
        j < circle.index;
        j++
      ) {
        const ns = Math.abs(slopes[j]);
        // bigger and same sign
        if (ns > cs && slopes[j] * slopes[cx] > 0) {
          cx = j;
          cs = ns;
        }
      }
      outLeft.push({ ...circle, index: cx });
    }
  }

  const outRight: Marker[] = [];

  for (let i = 0; i < outLeft.length; i++) {
    const circle = outLeft[i];
    if (
      !circle.isEndpoint &&
      !(i === outLeft.length - 1) &&
      outLeft[i + 1].isEndpoint &&
      outLeft[i + 1].index - circle.index < 40 &&
      pitches[circle.index] - pitches[outLeft[i + 1].index] > 1.5
    ) {
      // ignore
    } else {
      outRight.push(circle);
    }
  }

  return outRight;
};

const avgMaxMin = (arr: number[]) => {
  if (arr.length === 0) {
    console.error("programming error, array should not be empty");
  }

  let sum = 0;
  let max = Number.MIN_VALUE;
  let min = Number.MAX_VALUE;

  for (let i = 0; i < arr.length; i++) {
    sum += arr[i];
    if (arr[i] > max) {
      max = arr[i];
    }
    if (arr[i] < min) {
      min = arr[i];
    }
  }

  const avg = sum / arr.length;

  const sortedArr = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sortedArr.length / 2);
  const median =
    sortedArr.length % 2 === 0 ?
      (sortedArr[mid - 1] + sortedArr[mid]) / 2
    : sortedArr[mid];

  return { avg, max, min, median };
};
const renderWaveformsAsBlobsWhilePreservingXSymmetry = (
  isPlaying: boolean,
  waveform: number[],
  circles: Marker[],
  pitches: number[],
  [xCullL, xCullR]: [number, number],
  xZoomScale: (x: number) => number,
  yZoomScale: (y: number) => number
) => {
  const averages = new Array(pitches.length);
  const asAnchors = [
    ...(circles[0].index === 0 ? [] : [{ index: 0, isEndpoint: false }]),
    ...circles,
    ...(circles[circles.length - 1].index === pitches.length - 1 ?
      []
    : [{ index: pitches.length - 1, isEndpoint: false }]),
  ];
  for (let i = 0; i < asAnchors.length - 1; i++) {
    const leftOuterPoint = asAnchors[i];
    const rightOuterPoint = asAnchors[i + 1];
    const { leftInnerPoint, rightInnerPoint } = calcInnerPoints(
      leftOuterPoint.index,
      rightOuterPoint.index
    );
    // we only paint based on the inner points
    // as the outer points will often involve transitions and we don't
    // want the visualization to move around at those transition points
    // also, we tend to hear pitch more as a "sweet spot"
    // note that, as we are using only the mediam below, this
    // should be a no-op, but as during development we've also tested
    // using the average, it's good to keep this
    const sliced =
      rightInnerPoint > leftInnerPoint ?
        pitches.slice(leftInnerPoint, rightInnerPoint)
      : rightOuterPoint.index > leftOuterPoint.index ?
        pitches.slice(leftOuterPoint.index, rightOuterPoint.index)
      : [];

    const obj =
      sliced.length === 0 ?
        { avg: 0, max: 0, min: 0, median: 0, cix: doNotDrawMe }
      : { ...avgMaxMin(sliced), cix: leftOuterPoint.index };
    for (let j = leftOuterPoint.index; j < rightOuterPoint.index; j++) {
      averages[j] = obj;
    }
  }
  if (averages.length > 1)
    averages[averages.length - 1] = averages[averages.length - 2];

  const visualizedWaveform = [];
  let currentSegment: {
    x: number;
    y: number;
    vx: number;
    vy: number;
    cix: number;
    median: number;
    min: number;
    max: number;
  }[] = [];
  let currentCix: number = averages[0].cix;

  const waveformToPitch = scaleLinear({
    domain: [0, waveform.length - 1],
    range: [0, pitches.length - 1],
  });
  waveform.slice(xCullL, xCullR).forEach((y, i) => {
    const d = { x: i + xCullL, y };
    const pitchIndex = Math.round(waveformToPitch(d.x));
    const median = averages[pitchIndex].median;
    const min = averages[pitchIndex].max;
    const max = averages[pitchIndex].min;
    const cix: number = averages[pitchIndex].cix;
    const vy = (d.y > 0 ? 1 : -1) * Math.abs(d.y) ** 0.9 + median;

    const point = {
      ...d,
      vx: xZoomScale(d.x),
      vy,
      cix,
      median,
      min,
      max,
    };

    if (cix !== currentCix) {
      if (
        currentSegment.length > 0 &&
        !currentSegment.some((value) => value.vy === 0 || isNaN(value.vy)) &&
        currentSegment[currentSegment.length - 1].vx - currentSegment[0].vx > 0
      )
        visualizedWaveform.push(currentSegment);
      currentSegment = [];
      currentCix = cix;
    }
    currentSegment.push(point);
  });

  if (
    currentSegment.length > 0 &&
    !currentSegment.some(
      (value) =>
        value.vy === 0 ||
        value.y === 0 ||
        !isFinite(value.vy) ||
        value.cix === doNotDrawMe
    ) &&
    currentSegment[currentSegment.length - 1].vx - currentSegment[0].vx > 3
  ) {
    visualizedWaveform.push(currentSegment);
  }

  for (let i = 0; i < visualizedWaveform.length; i++)
    return visualizedWaveform
      .filter((segment) => segment.length > 0)
      .map((segment, index) => {
        const avg = lodash.meanBy(segment, (d) => d.vy);
        const points0 = [];
        const points1 = [];
        for (let ix = 0; ix < segment.length; ix++) {
          if (segment[ix].vy > avg) {
            points0.push({
              x: segment[ix].vx,
              y: yZoomScale(segment[ix].vy),
            });
            points1.unshift({
              x: segment[ix].vx,
              y: yZoomScale(2 * avg - segment[ix].vy),
            });
          } else {
            points0.push({
              x: segment[ix].vx,
              y: yZoomScale(2 * avg - segment[ix].vy),
            });
            points1.unshift({
              x: segment[ix].vx,
              y: yZoomScale(segment[ix].vy),
            });
          }
          if (
            !isFinite(points0[points0.length - 1].x) ||
            !isFinite(points0[points0.length - 1].y) ||
            !isFinite(points1[0].x) ||
            !isFinite(points1[0].y)
          )
            console.error("nan found", ix, avg, segment);
        }
        const points = [
          ...points0,
          ...points1,
          ...(points0.length > 0 ? [points0[0]]
          : points1.length > 0 ? [points1[0]]
          : []),
        ];
        return (
          <LinePath
            key={index}
            data={points}
            x={(d) => d.x}
            y={(d) => d.y}
            strokeOpacity={isPlaying ? 0.7 : 1.0}
            fillOpacity={isPlaying ? 0.7 : 1.0}
            stroke={audimeePrimary}
            fill={audimeePrimary}
            strokeWidth={1}
          />
        );
      });
};
const is1PointWithinBox = (
  points: { x: number; y: number }[],
  edge1: { x: number; y: number },
  edge2: { x: number; y: number }
): boolean => {
  // Determine the boundaries of the box
  const left = Math.min(edge1.x, edge2.x);
  const right = Math.max(edge1.x, edge2.x);
  const bottom = Math.min(edge1.y, edge2.y);
  const top = Math.max(edge1.y, edge2.y);

  // Early exit if all points are out of bounds by x
  if (points[0].x > right || points[points.length - 1].x < left) {
    return false;
  }

  // Iterate through the points and count how many are inside the box
  for (const point of points) {
    if (point.y === 0) continue;
    // we never want to use 0s
    // Skip points with x values outside the box
    if (point.x < left || point.x > right) {
      continue;
    }
    if (point.y >= bottom && point.y <= top) {
      return true;
    }
  }

  // Cannot finish early due to 0 condition
  return false;
};

const generatePianoKeys = (
  yZoomScale: ScaleLinear<number, number, never>,
  minY: number,
  maxY: number,
  leftIs: number,
  width: number
) => {
  const pianoKeys = [];
  const whiteKeys = ["C", "D", "E", "F", "G", "A", "B"];

  for (let i = Math.ceil(minY); i <= Math.floor(maxY); i++) {
    const octave = Math.floor(i / 12);
    const noteIndex = ((i % 12) + 12) % 12;
    const note = [
      "C",
      "C#",
      "D",
      "D#",
      "E",
      "F",
      "F#",
      "G",
      "G#",
      "A",
      "A#",
      "B",
    ][noteIndex];
    const isWhiteKey = whiteKeys.includes(note);
    const keyHeight = Math.abs(yZoomScale(i + 1) - yZoomScale(i));
    const yPosition = yZoomScale(i) - keyHeight / 2;
    const xStart = leftIs;
    const xEnd = leftIs + (isWhiteKey ? width : width / 2);
    const yStart =
      (
        note === "C" ||
        note === "D" ||
        note === "F" ||
        note === "G" ||
        note === "A"
      ) ?
        yPosition - keyHeight / 2
      : yPosition;
    const yEnd =
      yStart +
      (note === "B" || note === "E" || note === "C" || note === "F" ?
        (keyHeight * 3) / 2
      : note === "D" || note === "G" || note === "A" ? keyHeight * 2
      : keyHeight);

    pianoKeys.push({
      semitone: i,
      note,
      octave,
      isWhiteKey,
      keyHeight,
      xStart,
      xEnd,
      yStart,
      yEnd,
    });
  }

  return pianoKeys;
};

const drawPiano = (
  yZoomScale: ScaleLinear<number, number, never>,
  minY: number,
  maxY: number,
  leftIs: number,
  width: number,
  selectedKeys: number[],
  onDoubleClick: (st: number) => void
) => {
  const pianoKeys = generatePianoKeys(
    yZoomScale,
    Math.max(0, minY - 6),
    maxY + 6,
    leftIs,
    width
  );

  const mapped = pianoKeys.map(
    ({ note, octave, isWhiteKey, xStart, yStart, yEnd, semitone }) => ({
      elt: (
        <rect
          onDoubleClick={() => onDoubleClick(semitone)}
          key={`${note}${octave}`}
          x={xStart}
          y={yStart}
          width={isWhiteKey ? width : width / 2}
          height={yEnd - yStart}
          fill={
            lodash.includes(selectedKeys, semitone) && !isWhiteKey ?
              audimeePrimary
            : lodash.includes(selectedKeys, semitone) && isWhiteKey ?
              audimeeSecondary
            : note === "C" ?
              "#e0e0e0"
            : isWhiteKey ?
              "#f3f4f6"
            : "url(#blackToWhiteGradient)"
          }
          stroke="black"
        />
      ),
      isWhiteKey,
    })
  );
  return (
    <Group>
      {mapped.sort((a) => (a.isWhiteKey ? -1 : 1)).map((x) => x.elt)}
    </Group>
  );
};
const isZero = (i: number) => i === 0;
export const getSegmentationPoints = (semitoneData: number[]) => {
  const threshold = 0.29;
  const segmentationPoints = [];

  const semitoneDiff = semitoneData.map((val, i) =>
    i === 0 ? 0 : val - semitoneData[i - 1]
  );
  const significantChanges = semitoneDiff.map(
    (diff) => Math.abs(diff) > threshold
  );

  for (let i = 1; i < semitoneData.length - 1; i++) {
    if (
      (isZero(semitoneData[i + 1]) && !isZero(semitoneData[i])) ||
      (isZero(semitoneData[i - 1]) && !isZero(semitoneData[i]))
    ) {
      segmentationPoints.push({ index: i, isEndpoint: true });
    } else if (significantChanges[i]) {
      segmentationPoints.push({ index: i, isEndpoint: false });
    }
  }
  if (
    !isZero(semitoneData[0]) &&
    !isZero(semitoneData[1]) &&
    segmentationPoints.length > 0 &&
    segmentationPoints[0].index !== 0
  ) {
    segmentationPoints.unshift({ index: 0, isEndpoint: true });
  }

  if (
    !isZero(semitoneData[semitoneData.length - 1]) &&
    !isZero(semitoneData[semitoneData.length - 2]) &&
    segmentationPoints.length > 0 &&
    segmentationPoints[segmentationPoints.length - 1].index !==
      semitoneData.length - 1
  ) {
    segmentationPoints.push({
      index: semitoneData.length - 1,
      isEndpoint: true,
    });
  }

  return segmentationPoints;
};

const applyShift = (
  startPitch: number,
  startPitches: number[],
  pitches: number[],
  snapToKey: boolean,
  cumulativeDiff: number,
  leftOuterPoint: number,
  rightOuterPoint: number,
  abutting: { l: boolean; r: boolean }
) => {
  const { leftInnerPoint, rightInnerPoint } = calcInnerPoints(
    leftOuterPoint,
    rightOuterPoint
  );
  const { leftBleedPoint, rightBleedPoint } = calcBleedPoints(
    leftOuterPoint,
    rightOuterPoint,
    pitches.length
  );
  const start = leftBleedPoint;
  const end = Math.min(rightBleedPoint, startPitches.length);
  const diffy = snapToKey ? Math.round(cumulativeDiff) : cumulativeDiff;
  const leftShouldMove =
    abutting.l ||
    leftOuterPoint === 0 ||
    startPitches[leftOuterPoint - 1] === 0;
  const rightShouldMove =
    abutting.r ||
    rightOuterPoint === startPitches.length - 1 ||
    startPitches[rightOuterPoint + 1] === 0;

  const outPitches = [
    ...pitches.slice(0, start),
    ...startPitches.slice(start, end),
    ...pitches.slice(end),
  ];
  for (let index = start; index < end; index++) {
    const pitch = outPitches[index];
    if (pitch === 0) continue;

    const pitchShift =
      index <= leftInnerPoint && !leftShouldMove ?
        ((index - leftBleedPoint) / (leftInnerPoint - leftBleedPoint)) * diffy
      : index >= rightInnerPoint && !rightShouldMove ?
        (1 - (index - rightInnerPoint) / (rightBleedPoint - rightInnerPoint)) *
        diffy
      : diffy;

    outPitches[index] = pitch + pitchShift;
  }
  return outPitches;
};

const applyDriftReductionOrExpansion = (
  pitches: number[],
  leftOuterPoint: number,
  rightOuterPoint: number,
  pitchIndex: number,
  factor: number
) => {
  const midpoint = Math.max(
    leftOuterPoint + 1,
    Math.min(pitchIndex, rightOuterPoint - 1)
  );

  // Calculate the slope between leftOuterPoint and rightOuterPoint
  const slope =
    (pitches[rightOuterPoint] - pitches[leftOuterPoint]) /
    (rightOuterPoint - leftOuterPoint);

  const outPitches = pitches.slice();
  for (
    let index = leftOuterPoint;
    index < Math.min(rightOuterPoint + 1, outPitches.length);
    index++
  ) {
    const pitch = outPitches[index];
    if (pitch === 0) continue;

    const distanceToMidpoint = Math.abs(index - midpoint);
    const maxDistanceToMidpoint = Math.abs(leftOuterPoint - midpoint);

    // Determine the local effect based on the distance to the midpoint
    const localEffect = 1 - distanceToMidpoint / maxDistanceToMidpoint;

    // Calculate the target slope-adjusted pitch
    const basePitch =
      pitches[leftOuterPoint] + slope * (index - leftOuterPoint);
    const deviation = pitch - basePitch;
    const adjustment = deviation * factor * localEffect;

    outPitches[index] = pitch + adjustment;
  }
  return outPitches;
};

const smoothAdjustment = (x: number, a: number = 1) =>
  x * Math.exp(-(1 / (2 * Math.E * a * a)) * x * x);

const applyVibratoReductionOrExpansion = (
  pitches: number[],
  leftOuterPoint: number,
  rightOuterPoint: number,
  factor: number
) => {
  const nonZeroPitchesInRange = pitches
    .slice(leftOuterPoint, rightOuterPoint + 1)
    .filter((n) => n !== 0);
  // mathjs.mean throws on an empty array; nothing to adjust anyway
  if (nonZeroPitchesInRange.length === 0) return pitches.slice();
  const averagePitch = mathjs.mean(nonZeroPitchesInRange);
  // A non-finite mean would poison every pitch it is folded into
  if (!Number.isFinite(averagePitch)) return pitches.slice();

  const outPitches = pitches.slice();
  for (
    let index = leftOuterPoint;
    index < Math.min(rightOuterPoint + 1, outPitches.length);
    index++
  ) {
    const pitch = outPitches[index];
    if (pitch === 0) continue;

    const deviation = pitch - averagePitch;
    const adjustment = deviation * factor;
    const adjustedPitch = pitch + smoothAdjustment(adjustment);

    outPitches[index] = adjustedPitch;
  }
  return outPitches;
};

const applyDotShift = (
  pitches: number[],
  index: number,
  candidate0: number,
  candidate1: number,
  candidate2: number,
  candidate3: number,
  shiftDiff: number
) => {
  const candidate0Gap = Math.abs(candidate0 - index);
  const candidate1Gap = Math.abs(candidate1 - index);
  const candidate2Gap = Math.abs(candidate2 - index);
  const candidate3Gap = Math.abs(candidate3 - index);
  const candidates = [candidate0, candidate1, candidate2, candidate3];
  const candidateGaps = [
    candidate0Gap,
    candidate1Gap,
    candidate2Gap,
    candidate3Gap,
  ];
  const minIx = lodash.indexOf(candidateGaps, lodash.min(candidateGaps));
  const left = candidates[minIx - 1];
  const center = candidates[minIx];
  const right = candidates[minIx + 1];

  return pitches.map((pitch, ix) => {
    if (pitch === 0) return 0;
    if (ix < left || ix > right) return pitch;
    if (ix === center) return pitch + shiftDiff;
    if (ix < center) {
      return (
        scaleLinear({
          domain: [left, center],
          range: [0, shiftDiff],
        })(ix) + pitch
      );
    }
    return (
      scaleLinear({
        domain: [center, right],
        range: [shiftDiff, 0],
      })(ix) + pitch
    );
  });
};

const applyPitchChange = (
  circles: Marker[],
  mode: Mode,
  pitches: number[],
  shiftDiff: number,
  pitchIndex: PitchIndexInfo,
  startPitch: number,
  cumulativeDiff: number,
  startPitches: number[],
  abutting: { l: boolean; r: boolean }
) => {
  // Sort circles by index
  const index = Math.max(
    0,
    lodash.sortedIndexBy(
      circles,
      { index: pitchIndex.ix, isEndpoint: false },
      (v) => v.index
    ) - 1
  );
  const backAnchor = circles[index === 0 ? 0 : index - 1];
  const forwardAnchor =
    circles[index + 2 === circles.length ? circles.length - 1 : index + 2];
  const newPitches =
    mode === "pitch" ?
      [pitchIndex.leftOuterPoint, pitchIndex.rightOuterPoint].reduce(
        smoothAtKinks(startPitches, anchorBleed),
        applyShift(
          startPitch,
          startPitches,
          pitches,
          !(isHotkeyPressed("alt") || isHotkeyPressed("option")),
          cumulativeDiff,
          pitchIndex.leftOuterPoint,
          pitchIndex.rightOuterPoint,
          abutting
        )
      )
    : mode === "drift" ?
      applyDriftReductionOrExpansion(
        pitches,
        pitchIndex.leftOuterPoint,
        pitchIndex.rightOuterPoint,
        pitchIndex.ix,
        shiftDiff * 0.1
      )
    : mode === "vibrato" ?
      applyVibratoReductionOrExpansion(
        pitches,
        pitchIndex.leftOuterPoint,
        pitchIndex.rightOuterPoint,
        shiftDiff * 0.2
      )
    : applyDotShift(
        pitches,
        pitchIndex.ix,
        backAnchor.index,
        pitchIndex.leftOuterPoint,
        pitchIndex.rightOuterPoint,
        forwardAnchor.index,
        shiftDiff
      );
  return newPitches;
};

interface CustomGridBackgroundProps {
  scale: ScaleLinear<number, number>; // Type for a linear scale
  width: number; // Width of the grid
  tickValues: number[]; // Array of tick values
  getColor: (tickValue: number) => string; // Function to get color based on tick value
}
const CustomGridBackground: React.FC<CustomGridBackgroundProps> = ({
  scale,
  width,
  tickValues,
  getColor,
}) => {
  const rects = [];
  for (let i = 0; i < tickValues.length - 1; i++) {
    const y1 = scale(tickValues[i]);
    const y2 = scale(tickValues[i + 1]);
    const color = getColor(tickValues[i]);
    rects.push(
      <rect
        key={`grid-bg-${tickValues[i]}`}
        x={0}
        y={y1}
        width={width}
        height={y1 - y2}
        fill={color}
      />
    );
  }
  return <Group>{rects}</Group>;
};

export const newPitchChangeAlgo = (
  knobValue: number,
  validNotes: number[],
  pitches: number[],
  windowLength: number
) => {
  // Function to create windows and calculate shifts
  const calculateShift = (pitch: number, index: number): number => {
    const halfWindow = Math.floor(windowLength / 2);
    const windows: number[][] = [];

    // Create windows around the current pitch, centered on the index
    for (let i = -halfWindow; i <= halfWindow; i++) {
      const windowStart = index + i - halfWindow;
      const windowEnd = index + i + halfWindow;

      // Ensure the window is within bounds and filter out zeros
      const window = pitches
        .slice(
          Math.max(0, windowStart),
          Math.min(pitches.length, windowEnd + 1)
        )
        .filter((p) => p !== 0);
      if (window.length > 0) {
        windows.push(window);
      }
    }

    // Calculate shifts for each window
    const shifts = windows.map((window) => {
      const average = window.reduce((sum, val) => sum + val, 0) / window.length;
      const closestNote = findClosestNote(validNotes, average);
      const shift = closestNote + pitch - 2 * average;
      return shift;
    });

    // Calculate the average shift
    const averageShift =
      shifts.length === 0 ?
        0
      : shifts.reduce((sum, val) => sum + val, 0) / shifts.length;

    return averageShift;
  };

  // Process each pitch in the array
  const shiftedPitches = pitches.map((pitch, index) => {
    if (pitch === 0) {
      return 0; // Return 0 if the pitch is silence
    } else {
      const shift = calculateShift(pitch, index);
      // Apply the knob value to determine the final shift
      return pitch + shift * knobValue;
    }
  });

  return shiftedPitches;
};

const PitchCorrection = React.memo(
  ({
    audioUrl,
    actionName,
    namespace,
    keyNamespace,
    signedUrlModel,
    signedUrlAudio,
    disableDiatonicTranspose,
    userIsPaid,
    pitchGetter,
    originalPitchGetter,
    sessionSlugGetter,
    callbackPitchesToModal,
    track,
    // closeModalFromModal,
  }: {
    audioUrl: string;
    actionName: string;
    namespace: string;
    keyNamespace: string;
    signedUrlModel: string | null;
    signedUrlAudio: string | null;
    disableDiatonicTranspose: boolean;
    userIsPaid: boolean;
    pitchGetter: Promise<number[]>;
    originalPitchGetter: Promise<number[]>;
    sessionSlugGetter: Promise<string>;
    callbackPitchesToModal: (
      pitches: number[],
      setSubmitting: (b: boolean) => void
    ) => void;
    track: number;
    // closeModalFromModal: () => void;
  }) => {
    //// zzzzzzusssstand
    const usePitchCorrection = useNamedPitchCorrection((state) =>
      state.getNamedStore(namespace)
    );

    const pitchFuzzInCents = usePitchCorrection(
      (state) => state.pitchFuzzInCents
    );

    const useKey = useNamedKey((state) => state.getNamedStore(keyNamespace));
    const key = useKey((state) => state.key);
    const isProcessing = usePitchCorrection(
      (state) => state.processingCounter !== 0
    );
    const setIsProcessing = usePitchCorrection(
      (state) => (iProc: boolean) =>
        iProc ?
          state.incraeseProcessingCounter()
        : state.decreaseProcessingCounter()
    );
    const setCircles = usePitchCorrection((state) => state.setCircles);
    const setPitches = usePitchCorrection((state) => state.setPitches);
    const setOriginalPitches = usePitchCorrection(
      (state) => state.setOriginalPitches
    );
    const pitchesDirty = usePitchCorrection((state) => state.pitchesDirty);
    const setPitchesDirty = usePitchCorrection(
      (state) => state.setPitchesDirty
    );
    const setVibratoLevel = usePitchCorrection(
      (state) => state.setVibratoLevel
    );
    const setPitchLevel = usePitchCorrection((state) => state.setPitchLevel);
    const setPreviousVibratoLevel = usePitchCorrection(
      (state) => state.setPreviousVibratoLevel
    );
    const setPreviousPitchLevel = usePitchCorrection(
      (state) => state.setPreviousPitchLevel
    );

    const opts: YouTubeProps["opts"] = {
      height: "100%",
      width: "100%",
      playerVars: {
        // https://developers.google.com/youtube/player_parameters
        autoplay: 1,
        rel: 0,
      },
    };

    const reset = usePitchCorrection((state) => state.reset);
    const addToUndoStack_ = usePitchCorrection((state) => state.addToUndoStack);
    const setUndoStack_ = usePitchCorrection((state) => state.setUndoStack);
    const setRedoStack = usePitchCorrection((state) => state.setRedoStack);
    const addToUndoStack = (a: Undoable) => {
      addToUndoStack_(a);
      setRedoStack([]);
    };

    const setKey = useKey((state) => state.setKey);
    const circles = usePitchCorrection((state) => state.circles);
    const pitches = usePitchCorrection((state) => state.pitches);
    const originalPitches = usePitchCorrection(
      (state) => state.originalPitches
    );
    const vibratoLevel = usePitchCorrection((state) => state.vibratoLevel);
    const pitchLevel = usePitchCorrection((state) => state.pitchLevel);
    const undoStack = usePitchCorrection((state) => state.undoStack);
    const redoStack = usePitchCorrection((state) => state.redoStack);
    const decodedAudioDataUrl = usePitchCorrection(
      (state) => state.decodedAudioDataUrl
    );
    const setDecodedAudioDataUrl = usePitchCorrection(
      (state) => state.setDecodedAudioDataUrl
    );
    const decimations = usePitchCorrection((state) => state.decimations);
    const setDecimations = usePitchCorrection((state) => state.setDecimations);
    const audioDuration = usePitchCorrection((state) => state.duration);
    const setAudioDuration = usePitchCorrection((state) => state.setDuration);
    const useSelectedArtist = useNamedSelectedArtist((state) =>
      state.getNamedStore(namespace)
    );
    const artistId = useSelectedArtist((state) => state.selectedArtistId);
    ////// zzzzzzusssstand

    const handle = useFullScreenHandle();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const svgDiv = useRef<HTMLDivElement>(null);
    const { width: svgWidth, height: svgHeight } = useReactiveSvg(isFullScreen);
    const svgInnerWidth = svgWidth - margin.left - margin.right;
    const [waveform, setWaveform] = useState<number[]>([]);
    const [audioStartsAt, setAudioStartsAt] = useState<number>(0);
    const [sessionSlug, setSessionSlug] = useState<string | null>(null);
    const [audioSeekAsFractionOfAudioFile, setAudioSeekAsFractionOfAudioFile] =
      useState<number>(0);
    const [loadingIndex, setLoadingIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [pitchesAsFrequencyWithPadding, setPitchesAsFrequencyWithPadding] =
      useState<number[]>([]);
    const [pitchesLength, setPitchesLength] = useState<number>(0);
    const [pitchBaselineForPreview, setPitchBaselineForPreview] = useState<
      number[]
    >([]);
    // Snapshot of the last successful render — its exact wire-format contour
    // and its decoded audio — diffed against each edit and spliced onto for
    // single-segment re-renders. Refs, not state: nothing renders them, and
    // the runner reads them live so a debounce-delayed pass diffs against the
    // freshest baseline.
    const lastRenderedFrequencies = useRef<number[] | null>(null);
    const renderedAudioBuffer = useRef<AudioBuffer | null>(null);
    // The decoded audio the player runs off. Every successful render (full or
    // splice) lands here directly — the blob URL is only the initial-load and
    // remount fallback.
    const [playbackBuffer, setPlaybackBuffer] = useState<AudioBuffer | null>(
      null
    );
    const [toneJsStuff, setToneJsStuff] = useState<{
      player: Tone.Player;
      shifter: Tone.PitchShift;
    } | null>(null);
    const [toneJsStartInfo, setToneJsStartInfo] = useState<{
      time: number;
      leftIx: number;
      rightIx: number;
    } | null>(null);
    const [toneJsIsLoaded, setToneJsIsLoaded] = useState(false);

    const [shouldPlayPreviewSound, setShouldPlayPreviewSound] = useState(true);
    const [runnerIncr, setRunnerIncr] = useState<number>(0);
    // What the render request currently in flight covers — shared with the
    // runner so a new pass can serialize behind a different-segment render
    // instead of aborting it.
    const inFlightRender = useRef<InFlightRender | null>(null);
    // Always points at the freshest runner closure so work that outlives the
    // modal (the unmount flush below, a queued rerun fired by an in-flight
    // pass settling after close) never runs a stale pass.
    const runnerRef = useRef<() => Promise<void>>(async () => {});
    const isUnmountedRef = useRef(false);
    const hasPendingDebouncedRunRef = useRef(false);
    // The named store outlives this editor instance BY DESIGN (the unmount flush below lands late edits in it), but unset() hands it to the NEXT file. Freezing the session id this instance opened on lets every runner pass ask whether its writeback target is still this file — without the guard, a closed editor's in-flight render landed its audio URL in the store after unset(), and the next file's editor then skipped its own initial render and played the previous file's audio.
    const editingSessionIdRef = useRef(
      usePitchCorrection.getState().editingSessionId
    );
    const isRenderTargetCurrent = () =>
      usePitchCorrection.getState().editingSessionId ===
      editingSessionIdRef.current;
    // After unmount, setRunnerIncr is a no-op and the debounce timer is dead —
    // running the pass directly is the only way a rerun queued behind an
    // in-flight render can still happen. Its results land in the zustand
    // store, which outlives the modal and is what harmonies plays.
    const queueRunnerRerun = () => {
      if (isUnmountedRef.current) {
        runnerRef.current();
      } else {
        setRunnerIncr((i) => i + 1);
      }
    };
    const [pitchFetchAbortController, setPitchFetchAbortController] =
      useState<AbortController | null>(null);
    const [sufficientlyZoomedToBeEditable, setSufficientlyZoomedToBeEditable] =
      useState(false);
    const [ctrlInfo, setCtrlInfo] = useState<{
      start: { x: number; y: number };
      current: { x: number; y: number };
    } | null>(null);
    const [rawSvgMousePosition, setRawSvgMousePosition] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const [xZoom, setXZoom_] = useState<[number, number]>([0, 1]); // 15 fps
    const [yZoom, setYZoom_] = useState<[number, number]>([50, 62]); // 15 fps
    const minY = 7;
    const maxY = 142;
    // Cached decimations skip the decode, but with no markers yet (the editor was closed before its pitch data arrived) there is still nothing to show until the load effect has run
    const [loading, setLoading] = useState(
      decimations.length === 0 || circles.length === 0
    );
    // A silent or otherwise pitchless clip segments into zero markers. Every editor deref assumes at least one marker (circles[0]), so this is a terminal state for the mount: the overlay stays and the editor body is never used.
    const [hasNoPitchMarkers, setHasNoPitchMarkers] = useState(false);
    const [draggingCircleIndex, setDraggingCircleIndex] = useState<
      number | null
    >(null);
    const [initialCirclePosition, setInitialCirclePosition] = useState<
      number | null
    >(null);
    const [isOverCircle, setIsOverCircle] = useState(false);
    const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
    // The latest context CREATED but not yet adopted into the state above. setState after unmount is a no-op, so a context minted mid-load reaches the state-keyed closer only if the component survives the load; this ref is the net for the ones that never make it (see the orphan-closing cleanup below).
    const createdAudioContextRef = useRef<AudioContext | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [xRangeElt, setXRangeElt] = useState<HTMLDivElement | null>(null);
    const [yRangeElt, setYRangeElt] = useState<HTMLDivElement | null>(null);
    const [
      playheadPositionAsFractionOfAudioFile,
      setPlayheadPositionAsFractionOfAudioFile,
    ] = useState(0);
    const [svgMouseState, setSvgMouseState] = useState<{
      x: number;
      y: number;
      pitchIndices: PitchIndexInfo[];
      currentPitchIndex: number | null;
      startY: number;
      startPitches: number[];
      inMouseDown: boolean;
      inPitchChange: boolean;
    } | null>(null);
    const [panPosition, setPanPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const [nearestPitchIndexToMouse, setNearestPitchIndexToMouse] = useState<
      number | null
    >(null);
    const [mode, setMode] = useState<Mode>("pitch");
    const [xRangeMouseDownStart, setXRangeMouseDownStart] = useState<{
      pos: number;
      zoom0: number;
      zoom1: number;
    } | null>(null);
    const [yRangeMouseDownStart, setYRangeMouseDownStart] = useState<{
      pos: number;
      zoom0: number;
      zoom1: number;
    } | null>(null);

    // const setPricingModalOpen = usePricingModal((state) => state.setOpen);

    const [introVideoIsShowing, setIntroVideoIsShowing] = useState(false);

    const posthog = usePostHog();

    // The editor diffs each edit against the last render, requests just the
    // changed slice, and splices it onto the decoded base.
    const segmentRenderContext: SegmentRenderContext = {
      lastRenderedFrequencies,
      renderedAudioBuffer,
      setPlaybackBuffer,
    };

    const pitchAtBeginning = lodash.isEqual(pitches, originalPitches);

    const setWaveformDecimation = (l: number, r: number) => {
      let ctr = decimationMinimum;
      let i = 0;
      while (decimations.length) {
        if ((r - l) * audioDuration < ctr || i === decimations.length - 1) {
          setWaveform(decimations[i]);
          break;
        }
        ctr *= 2;
        i++;
      }
    };

    const setXZoom = useCallback(
      ([l, r]: [number, number]) => {
        const zoomSufficiency = determineZoomSufficiency(
          l,
          r,
          yZoom[0],
          yZoom[1],
          audioDuration
        );
        setSufficientlyZoomedToBeEditable(zoomSufficiency);
        setWaveformDecimation(l, r);
        setXZoom_(r - l < 0.03 ? [l, Math.min(l + 0.05, 1)] : [l, r]);
      },
      [setXZoom_, audioDuration, yZoom]
    );
    const setYZoom = useCallback(
      ([l, r]: [number, number]) => {
        const zoomSufficiency = determineZoomSufficiency(
          xZoom[0],
          xZoom[1],
          l,
          r,
          audioDuration
        );
        setSufficientlyZoomedToBeEditable(zoomSufficiency);
        setYZoom_(r - l < 1 ? [l, Math.min(l + 1, maxY)] : [l, r]);
      },
      [setYZoom_, maxY, audioDuration, xZoom]
    );
    const isPausedRef = useRef(false);
    const inThumb = useRef(false);
    const audioRef = useRef<HTMLAudioElement>(null);
    // make it wider than necessary
    const gridTickValues = Array.from(
      { length: Math.floor(maxY + 12) - Math.ceil(minY - 12) + 1 },
      (_, i) => i + Math.ceil(minY - 12) - 0.5
    );
    const getColor = (tickValue: number) => {
      const rounded = (Math.round(tickValue) - 1) % 12;
      return (
          rounded === 0 ||
            rounded === 2 ||
            rounded === 4 ||
            rounded === 5 ||
            rounded === 7 ||
            rounded === 9 ||
            rounded === 11
        ) ?
          "#f0f0f0"
        : "#e0e0e0";
    };

    // zooms

    const zoomLevel = xZoom[1] - xZoom[0];
    const xMarkerInterval = getXMarkerInterval(zoomLevel, audioDuration);
    const yZoomLevel = yZoom[1] - yZoom[0];
    const yMarkerInterval = getYMarkerInterval(yZoomLevel);

    const xZoomScale = scaleLinear({
      domain: [xZoom[0], xZoom[1]],
      range: [0, svgInnerWidth],
    });
    const xZoomGridScale = scaleLinear({
      domain: [xZoom[0] * svgInnerWidth, xZoom[1] * svgInnerWidth],
      range: [0, svgInnerWidth],
    });

    const yZoomScale = scaleLinear({
      domain: [yZoom[0], yZoom[1]],
      range: [svgHeight - margin.top - margin.bottom, 0],
    });

    const secondsScale = scaleLinear({
      domain: [xZoom[0] * audioDuration, xZoom[1] * audioDuration],
      range: [0, svgInnerWidth],
    });

    const rawSvgMousePositionInSvg =
      svgDiv.current && rawSvgMousePosition ?
        {
          x:
            rawSvgMousePosition.x - svgDiv.current.getBoundingClientRect().left,
          y: rawSvgMousePosition.y - svgDiv.current.getBoundingClientRect().top,
        }
      : null;

    useHotkeys(
      "ctrl",
      () => {
        if (rawSvgMousePositionInSvg) {
          setCtrlInfo({
            start: rawSvgMousePositionInSvg,
            current: rawSvgMousePositionInSvg,
          });
        }
      },
      [rawSvgMousePosition]
    );

    useHotkeys(
      "ctrl",
      () => {
        setCtrlInfo(null);
      },
      { keydown: false, keyup: true }
    );
    const depsHack = JSON.stringify({
      a: svgMouseState?.inMouseDown,
      b: svgMouseState?.currentPitchIndex,
      shouldPlayPreviewSound,
      toneJsIsLoaded,
      pitchesLength,
      audioDuration,
    });
    const getStartAndStopPointsForToneJsPreview = (
      li: number,
      ri: number,
      ad: number,
      pl: number,
      g: number = 0.05
    ) => {
      // Calculate initial start and stop points
      let start = (li * ad) / pl;
      let stop = (ri * ad) / pl;

      // Check if the gap is less than g
      if (stop - start > g) {
        // Find the average and adjust to ensure the duration is exactly g
        const avg = (start + stop) / 2;
        start = avg - g / 2;
        stop = avg + g / 2;
      }

      // Adjust li and ri based on the new start and stop values
      li = Math.round((start * pl) / ad);
      ri = Math.round((stop * pl) / ad);

      // Ensure li is at least one less than ri
      if (li >= ri) {
        li = ri - 1;
      }

      return [start, stop, li, ri];
    };

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === "z") {
          event.preventDefault();
          doUndo();
        } else if ((event.ctrlKey || event.metaKey) && event.key === "y") {
          event.preventDefault();
          doRedo();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    });
    useEffect(() => {
      if (
        toneJsStuff &&
        toneJsIsLoaded &&
        svgMouseState &&
        svgMouseState.inMouseDown &&
        svgMouseState.currentPitchIndex !== null &&
        audioDuration > 0 &&
        pitchesLength > 0 &&
        shouldPlayPreviewSound
      ) {
        const startInfo = {
          time: toneJsStuff.player.now(),
          leftIx:
            svgMouseState.pitchIndices[svgMouseState.currentPitchIndex]
              .leftOuterPoint,
          rightIx:
            svgMouseState.pitchIndices[svgMouseState.currentPitchIndex]
              .rightOuterPoint,
        };
        setToneJsStartInfo(startInfo);
        const [lp, rp] = getStartAndStopPointsForToneJsPreview(
          startInfo.leftIx,
          startInfo.rightIx,
          audioDuration,
          pitchesLength
        );
        toneJsStuff.player.loopStart = lp;
        toneJsStuff.player.loopEnd = rp;
        toneJsStuff.player.start();
        return () => {
          toneJsStuff.player.stop();
        };
      }
    }, [toneJsStuff, depsHack]);

    useEffect(() => {
      const f = () => {
        if (
          toneJsStuff &&
          toneJsStartInfo &&
          audioDuration > 0 &&
          pitchesLength > 0
        ) {
          const now = toneJsStuff.player.now();
          const [lt, rt, nli, nri] = getStartAndStopPointsForToneJsPreview(
            toneJsStartInfo.leftIx,
            toneJsStartInfo.rightIx,
            audioDuration,
            pitchesLength
          );
          const duration = (rt - lt) * audioDuration;
          const elapsed = (now - toneJsStartInfo.time) % duration;
          const pitchIx = lodash.clamp(
            Math.round(
              scaleLinear({
                domain: [0, duration],
                range: [nli, nri],
              })(elapsed)
            ),
            0,
            pitchesLength - 1
          );
          toneJsStuff.shifter.pitch =
            pitches[pitchIx] - pitchBaselineForPreview[pitchIx];
          if (elapsed > duration) {
            toneJsStuff.player.stop();
          }
        }
      };
      const iv = setInterval(f, 20);
      f();
      return () => clearInterval(iv);
    }, [
      toneJsStuff,
      toneJsStartInfo,
      pitchBaselineForPreview,
      pitches,
      audioDuration,
      pitchesLength,
    ]);
    useEffect(() => {
      // The preview player takes each render's decoded/spliced buffer directly
      // — no blob-URL fetch or re-decode per edit. The URL is the fallback for
      // the case where only the store-held URL survived a remount.
      const playbackSource = playbackBuffer ?? decodedAudioDataUrl;
      if (playbackSource !== null) {
        setToneJsIsLoaded(false);
        const shifter = new Tone.PitchShift().toDestination();
        const player = new Tone.Player({
          url: playbackSource,
          loop: true,
          autostart: false,
          onload: () => {
            setToneJsIsLoaded(true);
          },
        }).connect(shifter);
        // A buffer source is ready synchronously; onload only fires for URLs.
        if (player.loaded) {
          setToneJsIsLoaded(true);
        }
        setToneJsStuff({ player, shifter });
        return () => {
          try {
            player.dispose();
            shifter.dispose();
          } catch (e) {
            console.error(e);
          }
        };
      }
    }, [decodedAudioDataUrl, playbackBuffer]);
    useEffect(() => {
      // Adoption: this context reached state, so this closer owns it from here — the orphan cleanup below must not double-close it.
      if (
        audioContext !== null &&
        audioContext === createdAudioContextRef.current
      ) {
        createdAudioContextRef.current = null;
      }
      return () => {
        if (audioContext) {
          audioContext.close();
        }
      };
    }, [audioContext]);
    useEffect(() => {
      return () => {
        // A context created during a load the unmount interrupted: nothing else can reach it anymore, so close it here or it stays live for the document's life (the 7th live context makes AudioContext construction throw document-wide).
        const orphanAudioContext = createdAudioContextRef.current;
        if (orphanAudioContext !== null) {
          createdAudioContextRef.current = null;
          void closeAudioContextSilently(orphanAudioContext);
        }
      };
    }, []);
    useEffect(() => {
      hasPendingDebouncedRunRef.current = true;
      const timeout = setTimeout(() => {
        hasPendingDebouncedRunRef.current = false;
        runner();
      }, RUNNER_DEBOUNCE);
      return () => clearTimeout(timeout);
    }, [runnerIncr]);
    useEffect(() => {
      return () => {
        isUnmountedRef.current = true;
        // Closing the modal inside the debounce window would silently drop
        // the newest edit — the timer dies with the component while the pitch
        // line already shows the edit. Fire the pass now instead: it commits
        // to the store, and it skips itself when the contour is already
        // rendered or covered by the render in flight.
        if (hasPendingDebouncedRunRef.current) {
          runnerRef.current();
        }
      };
    }, []);
    useEffect(() => {
      if (!loading || hasNoPitchMarkers) return;
      const intervalId = setInterval(
        () => setLoadingIndex((index) => index + 1),
        10000
      );
      return () => clearTimeout(intervalId);
    }, [loading, hasNoPitchMarkers]);
    // no scroll
    useEffect(() => {
      const preventScroll = (event: WheelEvent) => {
        event.preventDefault();
      };

      window.addEventListener("wheel", preventScroll, { passive: false });

      return () => {
        window.removeEventListener("wheel", preventScroll);
      };
    }, []);
    // waveform load
    useEffect(() => {
      const loadWaveform = async () => {
        const { duration } = await (async () => {
          if (decimations.length === 0) {
            const {
              audioBuffer,
              audioContext: audioCtx,
              duration: audioUrlDuration,
            } = await loadWavFile(audioUrl, (createdAudioContext) => {
              // The fetch can outlive the component: a context minted after unmount has no cleanup left to run, so close it on the spot instead of tracking it.
              if (isUnmountedRef.current) {
                void closeAudioContextSilently(createdAudioContext);
                return;
              }
              createdAudioContextRef.current = createdAudioContext;
            });
            setAudioContext(audioCtx);
            const channelData = audioBuffer.getChannelData(0);
            // Create segmentation points
            const channelDataAsArray = Array.from(channelData);
            let decimationSize = Math.pow(
              2,
              Math.floor(mathjs.log2(svgInnerWidth))
            );
            const decimateSizes = [decimationSize];
            let decimationWindow = audioUrlDuration;
            while (decimationWindow >= decimationMinimum) {
              decimationWindow /= 2;
              decimationSize *= 2;
              decimateSizes.unshift(decimationSize);
            }
            const decimated = decimate(channelDataAsArray, decimateSizes).map(
              normalize
            );
            setDecimations(decimated);
            setWaveform(decimated[decimated.length - 1]);
            setAudioDuration(audioUrlDuration);
            return { duration: audioUrlDuration, audioCtx };
          } else {
            setWaveform(decimations[decimations.length - 1]);
            const audioCtx = new AudioContext();
            // Same net as the loadWavFile path: this async effect can already be past its unmount, and then setAudioContext is a no-op and no cleanup will ever see this context.
            if (isUnmountedRef.current) {
              void closeAudioContextSilently(audioCtx);
            } else {
              createdAudioContextRef.current = audioCtx;
            }
            setAudioContext(audioCtx);
            return { duration: audioDuration, audioCtx };
          }
        })();
        const [pitchData, s, originalPitchData] = await Promise.all([
          pitchGetter,
          sessionSlugGetter,
          originalPitchGetter,
        ]);
        setPitchesAsFrequencyWithPadding(pitchData);
        setSessionSlug(s);
        const semitoneData = pitchData
          .slice(300, -300)
          .map((frequency: number) =>
            frequency ? frequencyToSemitones(frequency) : 0
          );
        const originalSemitoneData = originalPitchData
          .slice(300, -300)
          .map((frequency: number) =>
            frequency ? frequencyToSemitones(frequency) : 0
          );

        // A silent or otherwise pitchless clip segments into zero markers; bail before the runner kicks off a conversion the editor could never show
        if (circles.length === 0) {
          const initialSegmentationPoints =
            getSegmentationPoints2(originalSemitoneData);
          if (initialSegmentationPoints.length === 0) {
            setHasNoPitchMarkers(true);
            setLoading(false);
            setPitches([]);
            setOriginalPitches([]);
            return;
          }
          setCircles(initialSegmentationPoints);
        }

        // fire off the runner right away
        if (!decodedAudioDataUrl) {
          runner_({
            sessionSlug: s,
            pitchFuzzInCents: pitchFuzzInCents,
            redeemSessionSlug:
              userIsPaid ?
                AI_BACKEND_URL_REDEEM_SESSION_SLUG
              : AI_BACKEND_URL_REDEEM_SESSION_SLUG_FOR_FREE,
            pitchFetchAbortController: null,
            setIsProcessing: setIsProcessing,
            setPitchFetchAbortController: setPitchFetchAbortController,
            pitchesAsFrequencyWithPadding: pitchData,
            pitches: semitoneData,
            setPitchBaselineForPreview: setPitchBaselineForPreview,
            artistId: artistId,
            signedUrlModel: signedUrlModel,
            decodedAudioData: decodedAudioDataUrl ? decodedAudioDataUrl : null,
            setDecodedAudioData: setDecodedAudioDataUrl,
            track: track,
            posthog: posthog,
            segmentRender: segmentRenderContext,
            inFlightRender: inFlightRender,
            queueRunnerRerun: queueRunnerRerun,
            isRenderTargetCurrent: isRenderTargetCurrent,
          })();
        }
        const nonZeroPitches = semitoneData.filter(
          (pitch: number) => pitch !== 0
        );
        // Pass the array itself: spreading thousands of pitches as arguments overflows the call stack. Empty only if stale markers survived a pitchless clip (the bail-out above covers the normal case), in which case the zoom centres on the pitch range
        const meanPitch =
          nonZeroPitches.length === 0 ?
            (minY + maxY) / 2
          : mathjs.mean(nonZeroPitches);
        setPitches(semitoneData);
        setPitchBaselineForPreview(semitoneData);
        setPitchesLength(semitoneData.length);
        setOriginalPitches(originalSemitoneData);
        setPitchesDirty(new Array(originalSemitoneData.length).fill(false));

        /// initial tonejs
        if (signedUrlAudio) {
          const shifter = new Tone.PitchShift().toDestination();
          const player = new Tone.Player({
            url: signedUrlAudio,
            loop: true,
            autostart: false,
            onload: () => {
              setToneJsIsLoaded(true);
            },
          }).connect(shifter);
          setToneJsStuff({ player, shifter });
        }

        ////
        // hack...
        // not sure why this is necessary, but the zoom handles
        // don't redraw correctly unless there is this slight stutter
        const doFinalStuff = () => {
          setXZoom([0, 1]);
          const initialZoomAvg = meanPitch;
          setYZoom([initialZoomAvg - 6, initialZoomAvg + 6]);
          const zoomSufficiency = determineZoomSufficiency(
            0,
            1,
            initialZoomAvg - 6,
            initialZoomAvg + 6,
            duration
          );
          setSufficientlyZoomedToBeEditable(zoomSufficiency);
          setLoading(false);
        };
        if (decimations.length > 0) {
          doFinalStuff();
        } else {
          setTimeout(doFinalStuff, 50);
        }
      };
      loadWaveform();
    }, []);

    const diatonicTranspose = makeDiatonicTranspose(key, namespace);
    const performDiatonicTranspose = (steps: number) => {
      const newPitches = diatonicTranspose(steps);
      setPitches(newPitches);
      setRunnerIncr((i) => i + 1);
    };
    const runner = runner_({
      sessionSlug: sessionSlug,
      pitchFetchAbortController: pitchFetchAbortController,
      pitchFuzzInCents: pitchFuzzInCents,
      redeemSessionSlug:
        userIsPaid ?
          AI_BACKEND_URL_REDEEM_SESSION_SLUG
        : AI_BACKEND_URL_REDEEM_SESSION_SLUG_FOR_FREE,
      setIsProcessing: setIsProcessing,
      setPitchFetchAbortController: setPitchFetchAbortController,
      pitchesAsFrequencyWithPadding: pitchesAsFrequencyWithPadding,
      pitches: pitches,
      setPitchBaselineForPreview: setPitchBaselineForPreview,
      artistId: artistId,
      signedUrlModel: signedUrlModel,
      decodedAudioData: decodedAudioDataUrl ? decodedAudioDataUrl : null,
      setDecodedAudioData: setDecodedAudioDataUrl,
      track: track,
      posthog: posthog,
      segmentRender: segmentRenderContext,
      inFlightRender: inFlightRender,
      queueRunnerRerun: queueRunnerRerun,
      isRenderTargetCurrent: isRenderTargetCurrent,
    });
    useEffect(() => {
      runnerRef.current = runner;
    });

    // animate playhead
    useEffect(() => {
      let animationFrameId: number;

      const updatePlayheadPosition = () => {
        if (audioContext && isPlaying && audioRef.current) {
          const playheadPos = audioRef.current.currentTime / audioDuration;
          setPlayheadPositionAsFractionOfAudioFile(playheadPos);

          animationFrameId = requestAnimationFrame(updatePlayheadPosition);
        }
      };

      if (isPlaying) {
        animationFrameId = requestAnimationFrame(updatePlayheadPosition);
      }

      return () => cancelAnimationFrame(animationFrameId);
    }, [
      isPlaying,
      audioContext,
      audioStartsAt,
      audioSeekAsFractionOfAudioFile,
      audioDuration,
    ]);

    // jump window
    useEffect(() => {
      if (isPlaying) {
        const playheadX = xZoomScale(playheadPositionAsFractionOfAudioFile);
        const w = svgInnerWidth;
        const visibleRange = xZoom[1] - xZoom[0];
        const jumpThreshold = xZoom[0] + visibleRange;
        if (playheadX > w * jumpThreshold * 2.0 && xZoom[1] < 1) {
          // can use primitive here
          setXZoom_(([start, end]) => {
            const newStart = Math.min(
              start + visibleRange / 3,
              1 - visibleRange
            );
            const newEnd = Math.min(end + visibleRange / 3, 1);
            return [newStart, newEnd];
          });
        }
      }
    }, [playheadPositionAsFractionOfAudioFile, isPlaying, xZoom, xZoomScale]);

    const pitchIndexScale = scaleLinear({
      domain: [0, 1],
      range: [0, pitches.length - 1],
    });

    const waveformNormScale = scaleLinear({
      domain: [0, waveform.length - 1],
      range: [0, 1],
    });

    const pitchIndexCull = lodash.flowRight([
      pitchIndexScale,
      xZoomScale.invert,
    ]);

    const waveformIndexCull = lodash.flowRight([
      waveformNormScale.invert,
      xZoomScale.invert,
    ]);

    const handleCircleMouseEnter = () => {
      setIsOverCircle(true);
    };

    const handleCircleMouseLeave = () => {
      setIsOverCircle(false);
    };

    const handleXZoomChange = (values: number[] | number) => {
      if (typeof values === "number") throw new Error("Invalid value type");
      setXZoom([values[0], values[1]]);
    };

    const handleYZoomChange = (values: number[]) => {
      setYZoom([values[0], values[1]]);
    };

    const runOnUsableIndexInPitchLine = (
      event: React.MouseEvent<SVGSVGElement>,
      f: (pitchIndex: number, yValue: number) => void
    ) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left - margin.left;
      const xIndex = xZoomScale.invert(x);
      const y = event.clientY - rect.top - margin.top;
      const yValue = yZoomScale.invert(y);
      const pitchIndex = Math.round(pitchIndexScale(xIndex));
      if (
        pitchIndex >= 0 &&
        pitchIndex < pitches.length &&
        pitches[pitchIndex] !== 0
      ) {
        f(pitchIndex, yValue);
        return true;
      } else {
        return false;
      }
    };

    const handleCircleMouseDown =
      (index: number) => (event: React.MouseEvent<SVGCircleElement>) => {
        // so that the SVG does not catch the mouse down
        if (mode !== "cut") return;
        event.stopPropagation();

        const circle = circles[index];
        if (circle.isEndpoint) return;
        setDraggingCircleIndex(index);
        setInitialCirclePosition(circle.index);
      };

    const handleSvgMouseDown = (event: React.MouseEvent<SVGSVGElement>) => {
      document.body.classList.add("select-none");
      // because handleCircleMouseDown stops propagation,
      // we can be sure that we are not clicking on a circle
      const y = event.clientY;
      const startY = y;
      const startPitches = pitches;

      const npitm = nearestPitchIndexToMouse ? nearestPitchIndexToMouse : -1;
      const index = Math.max(
        0,
        lodash.sortedIndexBy(
          circles,
          { index: npitm, isEndpoint: false },
          (v) => v.index
        ) - 1
      );
      const leftOuterPoint = circles[index];
      const rightOuterPoint = circles[index + 1];
      const newPitchIndex = {
        ix: npitm,
        leftOuterPoint: leftOuterPoint.index,
        rightOuterPoint: rightOuterPoint.index,
      };
      // not ideal to do the less-than-0 comparison
      // but it works as a convention, basically
      // we only add a pitch if we're actually near it
      const pitchesToAdd = newPitchIndex.ix < 0 ? [] : [newPitchIndex];

      // uniqBy because we only want one interval per click
      // otherwise we can click multiple times in the same interval
      const pitchIndices = lodash.uniqBy(
        svgMouseState && isHotkeyPressed("shift") ?
          [...svgMouseState.pitchIndices, ...pitchesToAdd]
        : svgMouseState ? svgMouseState.pitchIndices
        : !isHotkeyPressed("shift") && newPitchIndex.ix < 0 ? []
        : pitchesToAdd,
        (i) => i.leftOuterPoint
      );
      const curIx =
        pitchesToAdd.length === 0 ?
          -1
        : lodash.findIndex(
            pitchIndices,
            (i) => i.leftOuterPoint === pitchesToAdd[0].leftOuterPoint
          );
      setSvgMouseState({
        x: event.clientX,
        y,
        pitchIndices,
        currentPitchIndex: curIx < 0 ? null : curIx,
        startY,
        startPitches,
        inMouseDown: true,
        inPitchChange: svgMouseState ? svgMouseState.inPitchChange : false,
      });
    };

    const handleSvgMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
      setRawSvgMousePosition({ x: event.clientX, y: event.clientY });
      // first, we set the nearest pitch or null if we are not near anything
      // we do this always
      // we aren't panning, meaning that we're just
      // moving the mouse over the svg without grabbing anything
      // here, we always want to track the nearest pitch
      const wasAbleToSetNearestPitchIndex = runOnUsableIndexInPitchLine(
        event,
        (pitchIndex, yValue) => {
          // we are in the x vicinity of the pitch line
          // if we are in the y as well, set the nearest pitch index
          const yClosestPitch = pitches[pitchIndex];
          if (Math.abs(yValue - yClosestPitch) <= 2) {
            setNearestPitchIndexToMouse(pitchIndex);
          } else {
            setNearestPitchIndexToMouse(null);
          }
        }
      );
      if (!wasAbleToSetNearestPitchIndex) {
        // if there is no usable x, then we are not near a pitch line
        // and we can set the nearest pitch index to null
        setNearestPitchIndexToMouse(null);
      }
      if (isHotkeyPressed("ctrl")) {
        // The split points below read circles[0] and the last circle
        if (ctrlInfo && circles.length > 0) {
          const rect = event.currentTarget.getBoundingClientRect();
          const newCtrlInfo = {
            ...ctrlInfo,
            current: {
              x: event.clientX - rect.left,
              y: event.clientY - rect.top,
            },
          };
          setCtrlInfo(newCtrlInfo);
          const splitPoints = [
            ...(circles[0].index === 0 ? [] : [0]),
            ...circles.map((i) => i.index),
            ...(circles[circles.length - 1].index === pitches.length - 1 ?
              []
            : [pitches.length - 1]),
          ];
          const startX = Math.round(
            pitchIndexScale(xZoomScale.invert(ctrlInfo.start.x - margin.left))
          );
          const currentX = Math.round(
            pitchIndexScale(xZoomScale.invert(ctrlInfo.current.x - margin.left))
          );
          const startY = yZoomScale.invert(ctrlInfo.start.y - margin.top);
          const currentY = yZoomScale.invert(ctrlInfo.current.y - margin.top);
          const selectedRanges: PitchIndexInfo[] = [];
          for (let i = 0; i < splitPoints.length - 1; i++) {
            const toConsider = pitches
              .slice(splitPoints[i], splitPoints[i + 1] + 1)
              .map((pt, ix) => ({ y: pt, x: ix + splitPoints[i] }));
            const inBox = is1PointWithinBox(
              toConsider,
              { x: currentX, y: currentY },
              { x: startX, y: startY }
            );
            if (inBox) {
              selectedRanges.push({
                ix: Math.round(
                  (splitPoints[i + 1] - splitPoints[i]) / 2 + splitPoints[i]
                ),
                leftOuterPoint: splitPoints[i],
                rightOuterPoint: splitPoints[i + 1],
              });
            }
          }
          setSvgMouseState({
            x: event.clientX,
            y: event.clientY,
            pitchIndices: lodash.uniqBy(
              selectedRanges,
              (i) => i.leftOuterPoint
            ),
            startY,
            currentPitchIndex: null,
            startPitches: pitches,
            inMouseDown: false,
            inPitchChange: false,
          });
          return;
        }
      } else if (
        sufficientlyZoomedToBeEditable &&
        draggingCircleIndex !== null
      ) {
        // we are dragging a circle
        const iRan = runOnUsableIndexInPitchLine(event, (pitchIndex) => {
          // if we have a usable x,
          // update the dragged circle to the new index
          setCircles(
            circles.map((circle, i) =>
              i === draggingCircleIndex ?
                { index: pitchIndex, isEndpoint: false }
              : circle
            )
          );
        });
        if (!iRan) {
          // if the x is not usable, remove the circle and stop the drag
          // we may want to change this behavior
          setCircles(circles.filter((_, i) => i !== draggingCircleIndex));
          setDraggingCircleIndex(null);
          return;
        }
      }
      if (svgMouseState) {
        // we are not dragging a circle
        if (
          !isHotkeyPressed("shift") &&
          !svgMouseState.inPitchChange &&
          !nearestPitchIndexToMouse &&
          svgMouseState.pitchIndices.length === 0
        ) {
          // we are panning
          // calculate the new zoom and pan coordinates
          const rect = event.currentTarget.getBoundingClientRect();
          const curX = xZoomScale.invert(event.clientX - rect.left);
          const prevX = xZoomScale.invert(
            (panPosition ? panPosition.x : svgMouseState.x) - rect.left
          );
          const curY = yZoomScale.invert(event.clientY - rect.top);
          const prevY = yZoomScale.invert(
            (panPosition ? panPosition.y : svgMouseState.y) - rect.top
          );
          const xShift = curX - prevX;
          const yShift = curY - prevY;
          const newXZoom: [number, number] = [
            xZoom[0] - xShift,
            xZoom[1] - xShift,
          ];
          const newYZoom: [number, number] = [
            yZoom[0] - yShift,
            yZoom[1] - yShift,
          ];
          setXZoom(
            newXZoom[0] < 0 ? xZoom
            : newXZoom[1] > 1 ? xZoom
            : newXZoom
          );
          setYZoom(
            newYZoom[0] < minY ? yZoom
            : newYZoom[1] > maxY ? yZoom
            : newYZoom
          );
          setPanPosition({ x: event.clientX, y: event.clientY });
        } else if (svgMouseState.inMouseDown && !panPosition) {
          // we are moving a line segment
          const oldY = svgMouseState.y;
          const rect = event.currentTarget.getBoundingClientRect();

          const startY = svgMouseState.startY;
          const startPitches = svgMouseState.startPitches;

          const [pitchOldY, pitchNewY, pitchStartY] = [
            oldY,
            event.clientY,
            startY,
          ].map((y) => yZoomScale.invert(y - rect.top - margin.top));

          const pitchIndices = svgMouseState.pitchIndices.sort(
            (i) => i.leftOuterPoint
          );
          const pitchIndicesAbut: { l: boolean; r: boolean }[] = [];
          if (pitchIndices.length > 1) {
            for (let i = 0; i < pitchIndices.length; i++) {
              pitchIndicesAbut.push({
                l:
                  i === 0 ? false : (
                    doPitchIndicesAbut(pitchIndices[i - 1], pitchIndices[i])
                  ),
                r:
                  i === pitchIndices.length - 1 ?
                    false
                  : doPitchIndicesAbut(pitchIndices[i], pitchIndices[i + 1]),
              });
            }
          } else if (pitchIndices.length === 1) {
            pitchIndicesAbut.push({ l: false, r: false });
          }
          const newPitches = pitchIndices.reduce(
            (cv, pv, ix) =>
              applyPitchChange(
                circles,
                mode,
                cv,
                pitchNewY - pitchOldY,
                pv,
                pitchStartY,
                pitchNewY - pitchStartY,
                startPitches,
                pitchIndicesAbut[ix]
              ),
            pitches
          );
          addToUndoStack({
            time: new Date(),
            source: "edit",
            value: {
              pitches: pitches.slice(),
              vibratoLevel,
              pitchLevel,
              key,
            },
          });
          setPitchesDirty(
            pitchesDirty.map(
              (pd, i) =>
                pd ||
                pitchIndices.some(
                  ({ leftOuterPoint, rightOuterPoint }) =>
                    i >= leftOuterPoint && i < rightOuterPoint
                )
            )
          );
          setPitches(newPitches);

          setSvgMouseState({
            x: event.clientX,
            y: event.clientY,
            pitchIndices,
            startY,
            currentPitchIndex: svgMouseState.currentPitchIndex,
            startPitches,
            inMouseDown: true,
            inPitchChange: true,
          });
        }
      }
    };

    const handleSvgMouseUp = () => {
      document.body.classList.remove("select-none");
      if (
        !panPosition &&
        !isHotkeyPressed("shift") &&
        !isHotkeyPressed("ctrl") &&
        svgMouseState &&
        svgMouseState.pitchIndices.length > 0
      ) {
        // a crude test for when we should run the runner
        setRunnerIncr((i) => i + 1);
      }
      setPanPosition(null);
      // we clear the mouse state if we're not in a multiselect and if there aren't other selected pitches
      if (
        !isHotkeyPressed("shift") &&
        (svgMouseState?.pitchIndices?.length || 0) < 2
      )
        setSvgMouseState(null);
      // otherwise if we're in a pitch change we retain the state but change
      // to not being in a pitch change
      else if (
        svgMouseState !== null &&
        (svgMouseState.inPitchChange || isHotkeyPressed("shift"))
      ) {
        setSvgMouseState({
          ...svgMouseState,
          inMouseDown: false,
          inPitchChange: false,
        });
        // otherwise we clear the state as we've clicked outside of the
        // pitch area
      } else if (svgMouseState !== null) {
        setSvgMouseState(null);
      }
      if (
        !isHotkeyPressed("shift") &&
        sufficientlyZoomedToBeEditable &&
        draggingCircleIndex !== null &&
        initialCirclePosition !== null
      ) {
        // we move the circle
        const currentCircle = circles[draggingCircleIndex];

        if (currentCircle.index === initialCirclePosition) {
          // Remove the circle if the position hasn't changed
          const newCircles = circles.filter(
            (_, i) => i !== draggingCircleIndex
          );
          addToUndoStack({
            time: new Date(),
            source: "edit",
            value: circles.slice(),
          });
          if (!lodash.isEqual(newCircles, circles)) setCircles(newCircles);
          setIsOverCircle(false);
        }
      } else if (
        !isHotkeyPressed("shift") &&
        mode === "cut" &&
        sufficientlyZoomedToBeEditable &&
        nearestPitchIndexToMouse !== null &&
        draggingCircleIndex === null &&
        (!svgMouseState || !svgMouseState.inPitchChange)
      ) {
        // we add a circle
        const newCircles = [
          ...circles,
          { index: nearestPitchIndexToMouse, isEndpoint: false },
        ].sort((a, b) => a.index - b.index);
        addToUndoStack({
          time: new Date(),
          source: "edit",
          value: circles.slice(),
        });
        setCircles(newCircles);
      }

      setDraggingCircleIndex(null);
      setInitialCirclePosition(null);
    };

    const handlePlay = async (audioSeekFraction?: number) => {
      const newDecodedAudioData = decodedAudioDataUrl;

      if (newDecodedAudioData && audioContext && audioRef.current) {
        isPausedRef.current = false;
        audioRef.current.currentTime =
          (audioSeekFraction === undefined ?
            audioSeekAsFractionOfAudioFile
          : audioSeekFraction) * audioDuration;
        audioRef.current.play();
        setAudioStartsAt(audioContext.currentTime);
        setIsPlaying(true);
      }
    };

    const handleSeek = (timeAsFractionOfAudioFile: number) => {
      if (audioDuration) {
        setPlayheadPositionAsFractionOfAudioFile(timeAsFractionOfAudioFile);
        setAudioSeekAsFractionOfAudioFile(timeAsFractionOfAudioFile);
      }
    };

    const handlePause = () => {
      if (audioContext && audioRef.current) {
        isPausedRef.current = true;
        audioRef.current.pause();
        const seekTime =
          (audioContext.currentTime -
            audioStartsAt +
            audioSeekAsFractionOfAudioFile * audioDuration) /
          audioDuration;
        setAudioSeekAsFractionOfAudioFile(seekTime);
        setIsPlaying(false);
      }
    };

    const handleStop = () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      isPausedRef.current = false;
      setAudioSeekAsFractionOfAudioFile(0);
      setIsPlaying(false);
      setPlayheadPositionAsFractionOfAudioFile(0);
      setXZoom([0, xZoom[1] - xZoom[0]]); // Reset zoom to the beginning
    };

    useHotkeys(
      "space",
      isProcessing || !decodedAudioDataUrl ? () => {}
      : isPlaying ? handlePause
      : () => handlePlay(),
      [
        isPlaying,
        decodedAudioDataUrl,
        audioStartsAt,
        isProcessing,
        audioSeekAsFractionOfAudioFile,
        audioDuration,
      ]
    );

    const movePlayheadCommon = (
      e: React.MouseEvent<SVGRectElement, MouseEvent>
    ) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const seekPosition = xZoomScale.invert(clickX);
      handleSeek(seekPosition);
      return seekPosition;
    };

    const pitchLabels = generatePitchLabels(minY, maxY, yMarkerInterval);
    const restoreUndoable = (undoable: Undoable) => {
      if ("pitches" in undoable.value) {
        setPitches(undoable.value.pitches);
        setVibratoLevel(undoable.value.vibratoLevel);
        setPreviousVibratoLevel(undoable.value.vibratoLevel);
        setPitchLevel(undoable.value.pitchLevel);
        setPreviousPitchLevel(undoable.value.pitchLevel);
        setKey(undoable.value.key);
        setRunnerIncr((i) => i + 1);
      } else {
        setCircles(undoable.value);
      }
    };
    const undoTransitionInputs = (): UndoTransitionInputs => ({
      undoStack,
      redoStack,
      currentPitchState: { pitches, vibratoLevel, pitchLevel, key },
      currentCircles: circles,
      now: new Date(),
    });
    const applyUndoTransition = (transition: UndoTransition | null) => {
      if (transition === null) {
        return;
      }
      setUndoStack_(transition.undoStack);
      setRedoStack(transition.redoStack);
      restoreUndoable(transition.restored);
    };
    const doRedo = () =>
      applyUndoTransition(computeRedoTransition(undoTransitionInputs()));
    const doUndo = () =>
      applyUndoTransition(computeUndoTransition(undoTransitionInputs()));
    return svgHeight < 250 || svgWidth < 550 ?
        <div className="flex w-full flex-col">
          <Dialog.Title
            as="h3"
            className="mb-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
          >
            Screen too small
          </Dialog.Title>
          <NormalText elementType="p" className="mb-1">
            It looks like your screen is too small to use the pitch editor
          </NormalText>
          <NormalText elementType="p">
            Please open Audimee on a device with a larger screen.
          </NormalText>
        </div>
      : <FullScreen
          handle={handle}
          onChange={(state) => {
            setIsFullScreen(state);
          }}
        >
          <div
            className={classNames(
              "flex flex-col bg-white",
              ...(isFullScreen ? ["p-8"] : [])
            )}
            onMouseMove={makeInactiveOnCondition(
              loading || isPlaying,
              (event) => {
                if (xRangeMouseDownStart !== null && xRangeElt !== null) {
                  const rect = xRangeElt.getBoundingClientRect();
                  const x = (event.clientX - rect.left) / rect.width;
                  const x0 =
                    xRangeMouseDownStart.zoom0 + x - xRangeMouseDownStart.pos;
                  const x1 =
                    xRangeMouseDownStart.zoom1 + x - xRangeMouseDownStart.pos;
                  if (x0 < 0)
                    setXZoom([
                      0,
                      xRangeMouseDownStart.zoom1 - xRangeMouseDownStart.zoom0,
                    ]);
                  else if (x1 >= 1)
                    setXZoom([
                      1 -
                        (xRangeMouseDownStart.zoom1 -
                          xRangeMouseDownStart.zoom0),
                      1,
                    ]);
                  else setXZoom([x0, x1]);
                }
                if (yRangeMouseDownStart !== null && yRangeElt !== null) {
                  const rect = yRangeElt.getBoundingClientRect();
                  const y = (event.clientY - rect.top) / rect.height;
                  const yPos = scaleLinear({
                    domain: [0, 1],
                    range: [maxY, minY],
                  })(y);
                  const y0 =
                    yRangeMouseDownStart.zoom0 +
                    (yPos - yRangeMouseDownStart.pos);
                  const y1 =
                    yRangeMouseDownStart.zoom1 +
                    (yPos - yRangeMouseDownStart.pos);

                  if (y0 < minY)
                    setYZoom([
                      minY,
                      minY +
                        (yRangeMouseDownStart.zoom1 -
                          yRangeMouseDownStart.zoom0),
                    ]);
                  else if (y1 > maxY)
                    setYZoom([
                      maxY -
                        (yRangeMouseDownStart.zoom1 -
                          yRangeMouseDownStart.zoom0),
                      maxY,
                    ]);
                  else setYZoom([y0, y1]);
                }
              }
            )}
            onMouseUp={makeInactiveOnCondition(loading || isPlaying, () => {
              inThumb.current = false;
              setXRangeMouseDownStart(null);
              setYRangeMouseDownStart(null);
            })}
          >
            <audio
              ref={audioRef}
              preload="auto"
              src={decodedAudioDataUrl ? decodedAudioDataUrl : undefined}
              className="hidden"
              onEnded={() => {
                if (!isPausedRef.current) {
                  setIsPlaying(false);
                  setPlayheadPositionAsFractionOfAudioFile(0);
                  setAudioSeekAsFractionOfAudioFile(0);
                }
              }}
            ></audio>
            <div
              className={classNames(
                "flex w-full items-end justify-between gap-4 pb-2",
                ...(introVideoIsShowing ? ["invisible"] : [])
              )}
            >
              {/**className="grid w-full grid-cols-[1fr_max-content_min-content] items-center gap-x-4 pt-2 md:grid-cols-[1fr_max-content_1fr]" */}
              <div className="grid grid-cols-5 gap-2 md:grid-cols-8">
                <button
                  tabIndex={-1}
                  onClick={makeInactiveOnCondition(
                    loading,
                    isProcessing || !decodedAudioDataUrl ? () => {}
                    : isPlaying ? handlePause
                    : () => handlePlay()
                  )}
                >
                  {loading || isProcessing || !decodedAudioDataUrl ?
                    <div
                      className={`relative flex h-7 w-7 items-center justify-center`}
                    >
                      <PlayCircleIcon
                        className={`absolute h-7 w-7 stroke-gray-400`}
                      />
                      <LoadingSpinner className="absolute h-7 w-7" />
                    </div>
                  : isPlaying ?
                    <PauseCircleIcon
                      className={classNames(
                        "h-7 w-7",
                        isProcessing || loading || !decodedAudioDataUrl ?
                          "stroke-gray-400"
                        : "stroke-gray-900"
                      )}
                    />
                  : <PlayCircleIcon
                      className={classNames(
                        "h-7 w-7",
                        isProcessing || loading || !decodedAudioDataUrl ?
                          "stroke-gray-400"
                        : "stroke-gray-900"
                      )}
                    />
                  }
                </button>
                <button
                  tabIndex={-1}
                  onClick={makeInactiveOnCondition(
                    loading,
                    isProcessing || !decodedAudioDataUrl ? () => {} : handleStop
                  )}
                >
                  <StopCircleIcon
                    className={classNames(
                      "h-7 w-7",
                      isProcessing || loading || !decodedAudioDataUrl ?
                        "stroke-gray-400"
                      : "stroke-gray-900"
                    )}
                  />
                </button>
                <button
                  tabIndex={-1}
                  onClick={() => {
                    setShouldPlayPreviewSound(!shouldPlayPreviewSound);
                  }}
                >
                  {shouldPlayPreviewSound ?
                    <SpeakerWaveIcon className={`h-7 w-7 stroke-gray-900`} />
                  : <SpeakerXMarkIcon className={`h-7 w-7 stroke-gray-900`} />}
                </button>
                <button
                  tabIndex={-1}
                  onClick={makeInactiveOnCondition(
                    loading || isPlaying,
                    doUndo
                  )}
                  disabled={undoStack.length === 0}
                >
                  <ArrowUturnLeftIcon
                    className={classNames(
                      "h-7 w-7",
                      undoStack.length === 0 ?
                        "stroke-gray-400"
                      : "stroke-gray-900"
                    )}
                  />
                </button>

                <button
                  tabIndex={-1}
                  onClick={makeInactiveOnCondition(
                    loading || isPlaying,
                    doRedo
                  )}
                  disabled={redoStack.length === 0}
                >
                  <ArrowUturnRightIcon
                    className={classNames(
                      "h-7 w-7",
                      redoStack.length === 0 ?
                        "stroke-gray-400"
                      : "stroke-gray-900"
                    )}
                  />
                </button>
                <button
                  onClick={() => {
                    reset();
                    setRunnerIncr((i) => i + 1);
                  }}
                >
                  <XCircleIcon
                    className={classNames(
                      "h-7 w-7",
                      pitchAtBeginning ? "stroke-gray-400" : "stroke-gray-900"
                    )}
                  />
                </button>
                <button onClick={isFullScreen ? handle.exit : handle.enter}>
                  {isFullScreen ?
                    <ArrowsPointingInIcon className="h-7 w-7 stroke-gray-900" />
                  : <ArrowsPointingOutIcon className="h-7 w-7 stroke-gray-900" />
                  }
                </button>
                {!loading && (
                  <QuestionMarkCircleIcon
                    data-tooltip-id={"got-a-question?"}
                    className="h-7 w-7 stroke-gray-900"
                  />
                )}
              </div>
              {/* {loading || userIsPaid ?
                <></>
              : <div>
                  <SoftText elementType="p">
                    <LinkButton
                      onClick={() => {
                        closeModalFromModal();
                        setPricingModalOpen();
                      }}
                    >
                      Upgrade
                    </LinkButton>{" "}
                    for faster previews.
                  </SoftText>
                </div>
              } */}
              {loading ?
                <></>
              : <div className="flex items-end gap-4 pr-4">
                  {!disableDiatonicTranspose && (
                    <div className="flex flex-col items-center">
                      <div className="flex select-none flex-row items-center gap-2">
                        <button
                          onClick={() => {
                            performDiatonicTranspose(-1);
                          }}
                        >
                          <MinusCircleIcon
                            className={`h-7 w-7 stroke-primary`}
                          />
                        </button>
                        <button
                          onClick={() => {
                            performDiatonicTranspose(1);
                          }}
                        >
                          <PlusCircleIcon
                            className={`h-7 w-7 stroke-primary`}
                          />
                        </button>
                      </div>
                      <span className="select-none text-xs">Transpose</span>
                    </div>
                  )}
                  <div className="flex flex-row items-center gap-4 pr-4">
                    <div className="flex flex-col items-center">
                      <VibratoKnob
                        namespace={namespace}
                        keyNamespace={keyNamespace}
                        inactiveCondition={loading}
                        onEnd={() => {
                          setRunnerIncr((i) => i + 1);
                        }}
                      />
                      <span className="select-none text-xs">De-vibrato</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <PitchKnob
                        namespace={namespace}
                        keyNamespace={keyNamespace}
                        inactiveCondition={loading}
                        onEnd={() => {
                          setRunnerIncr((i) => i + 1);
                        }}
                      />
                      <span className="select-none text-xs">Pitch</span>
                    </div>
                    <PitchSelectMenu
                      noKeyIsAnOption={true}
                      namespace={keyNamespace}
                    />
                  </div>
                </div>
              }
            </div>
            <div className="flex w-full grow flex-row">
              {introVideoIsShowing ?
                <div style={{ width: svgWidth, height: svgHeight }}>
                  <YouTube
                    videoId={"F1jQq9hysTs"}
                    opts={opts}
                    className="mx-auto"
                    style={{
                      maxWidth: svgWidth,
                      maxHeight: svgHeight,
                      aspectRatio: "16 / 9",
                    }}
                  />
                </div>
              : <div
                  className={"relative grow"}
                  ref={svgDiv}
                  onWheel={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;
                    const zoomIn = e.deltaY > 0 ? 1 : -1;
                    const yStuff = () => {
                      const yValue =
                        (y - margin.top) /
                        (rect.height - margin.top - margin.bottom);
                      const zoomCandidate: [number, number] = [
                        lodash.clamp(
                          yZoom[0] +
                            zoomFactorForWheelY(yZoom) * zoomIn * (1 - yValue),
                          minY,
                          maxY
                        ),
                        lodash.clamp(
                          yZoom[1] -
                            zoomFactorForWheelY(yZoom) * zoomIn * yValue,
                          minY,
                          maxY
                        ),
                      ];
                      setYZoom(
                        zoomCandidate[1] - zoomCandidate[0] < 1.0 ?
                          yZoom
                        : zoomCandidate
                      );
                    };

                    const xStuff = () => {
                      const xValue =
                        (x - margin.left) /
                        (rect.width - margin.left - margin.right);
                      const zoomCandidate: [number, number] = [
                        lodash.clamp(
                          xZoom[0] + zoomFactorForWheelX * zoomIn * xValue,
                          0,
                          1
                        ),
                        lodash.clamp(
                          xZoom[1] -
                            zoomFactorForWheelX * zoomIn * (1 - xValue),
                          0,
                          1
                        ),
                      ];
                      setXZoom(
                        zoomCandidate[1] - zoomCandidate[0] < 0.03 ?
                          xZoom
                        : zoomCandidate
                      );
                    };
                    if (x < 40) {
                      yStuff();
                    } else {
                      xStuff();
                    }
                  }}
                >
                  <svg
                    width={svgWidth}
                    height={svgHeight}
                    onMouseDown={makeInactiveOnCondition(
                      loading || isPlaying,
                      handleSvgMouseDown
                    )}
                    onMouseMove={makeInactiveOnCondition(
                      loading || isPlaying,
                      handleSvgMouseMove
                    )}
                    onMouseUp={makeInactiveOnCondition(
                      loading || isPlaying,
                      handleSvgMouseUp
                    )}
                    onMouseLeave={makeInactiveOnCondition(
                      loading || isPlaying,
                      () => {
                        setCtrlInfo(null);
                        handleSvgMouseUp();
                      }
                    )}
                    className={classNames(
                      "block",
                      (
                        rawSvgMousePositionInSvg &&
                          rawSvgMousePositionInSvg.x < margin.left
                      ) ?
                        "cursor-default"
                      : ctrlInfo ? "cursor-crosshair"
                      : panPosition !== null ? "cursor-grabbing"
                      : (
                        isOverCircle ||
                        nearestPitchIndexToMouse !== null ||
                        draggingCircleIndex !== null
                      ) ?
                        "cursor-pointer"
                      : "cursor-grab"
                    )}
                  >
                    {" "}
                    <defs>
                      <linearGradient
                        id="blackToWhiteGradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                      >
                        <stop
                          offset="0%"
                          style={{ stopColor: "#000000", stopOpacity: 1 }}
                        />
                        <stop
                          offset="100%"
                          style={{ stopColor: "#6b7280", stopOpacity: 1 }}
                        />
                      </linearGradient>
                    </defs>
                    <Group left={margin.left} top={margin.top}>
                      <CustomGridBackground
                        scale={yZoomScale}
                        width={svgInnerWidth}
                        tickValues={gridTickValues}
                        getColor={getColor}
                      />
                      <GridRows
                        scale={yZoomScale}
                        width={svgInnerWidth}
                        stroke="#e0e0e0"
                        tickValues={gridTickValues}
                      />
                      <GridColumns
                        scale={xZoomGridScale}
                        height={svgHeight - margin.top - margin.bottom}
                        stroke="#e0e0e0"
                      />

                      {waveform.length > 0 &&
                        pitches.length > 0 &&
                        // renderWaveformsAsBlobs... derefs circles[0]
                        circles.length > 0 &&
                        renderWaveformsAsBlobsWhilePreservingXSymmetry(
                          isPlaying,
                          waveform,
                          circles,
                          pitches,
                          [
                            Math.floor(waveformIndexCull(0)),
                            Math.floor(waveformIndexCull(svgInnerWidth)),
                          ],
                          lodash.flowRight([xZoomScale, waveformNormScale]),
                          yZoomScale
                        )}

                      {sufficientlyZoomedToBeEditable &&
                        renderPitchLines(
                          pitches,
                          svgMouseState?.pitchIndices || [],
                          [
                            Math.floor(pitchIndexCull(0)),
                            Math.floor(pitchIndexCull(svgInnerWidth)),
                          ],
                          mode === "cut",
                          lodash.flowRight([
                            xZoomScale,
                            pitchIndexScale.invert,
                          ]),
                          yZoomScale
                        )}

                      {sufficientlyZoomedToBeEditable &&
                        mode === "cut" &&
                        circles.map((circle, index) => {
                          const circleX = pitchIndexScale.invert(circle.index);
                          const circleY = pitches[circle.index];
                          return (
                            <Circle
                              className="animate-pulse"
                              key={index}
                              cx={xZoomScale(circleX)}
                              cy={yZoomScale(circleY)}
                              r={4}
                              fill={"#ffd1a1"}
                              onMouseDown={makeInactiveOnCondition(
                                loading ||
                                  isPlaying ||
                                  !sufficientlyZoomedToBeEditable,
                                handleCircleMouseDown(index)
                              )}
                              onMouseEnter={makeInactiveOnCondition(
                                loading ||
                                  isPlaying ||
                                  !sufficientlyZoomedToBeEditable,
                                handleCircleMouseEnter
                              )}
                              onMouseLeave={makeInactiveOnCondition(
                                loading ||
                                  isPlaying ||
                                  !sufficientlyZoomedToBeEditable,
                                handleCircleMouseLeave
                              )}
                            />
                          );
                        })}
                      {ctrlInfo && (
                        <rect
                          x={Math.min(
                            ctrlInfo.current.x - margin.left,
                            ctrlInfo.start.x - margin.left
                          )}
                          y={Math.min(
                            ctrlInfo.current.y - margin.top,
                            ctrlInfo.start.y - margin.top
                          )}
                          width={Math.abs(
                            ctrlInfo.current.x - ctrlInfo.start.x
                          )}
                          height={Math.abs(
                            ctrlInfo.current.y - ctrlInfo.start.y
                          )}
                          fill={audimeeSecondary}
                          opacity={0.3}
                          stroke={audimeePrimary}
                          strokeWidth={2}
                        />
                      )}

                      {playheadPositionAsFractionOfAudioFile > 0 && (
                        <Line
                          from={{
                            x: xZoomScale(
                              playheadPositionAsFractionOfAudioFile
                            ),
                            y: -margin.top,
                          }}
                          to={{
                            x: xZoomScale(
                              playheadPositionAsFractionOfAudioFile
                            ),
                            y: svgHeight - margin.top - margin.bottom,
                          }}
                          stroke="#80578a"
                          strokeWidth={2}
                        />
                      )}

                      <rect
                        x={-margin.left}
                        y={0}
                        width={margin.left}
                        height={svgHeight - margin.top - 20}
                        fill="white"
                      />
                      {drawPiano(
                        yZoomScale,
                        minY,
                        maxY,
                        -margin.left,
                        margin.left,
                        [],
                        (n) => {
                          const newPitches = calculatePitchesForPitchChange(
                            100,
                            [n],
                            circles,
                            0,
                            pitches
                          );
                          addToUndoStack({
                            time: new Date(),
                            source: "edit",
                            value: {
                              pitches: pitches.slice(),
                              vibratoLevel,
                              pitchLevel,
                              key,
                            },
                          });
                          setPitches(newPitches);
                          setRunnerIncr((i) => i + 1);
                        }
                      )}
                      {pitchLabels.map(({ pitch, value }) => (
                        <Text
                          key={pitch}
                          className="pointer-events-none select-none"
                          x={pitchLabelOffset}
                          y={yZoomScale(value)}
                          dy=".32em"
                          fontSize={10}
                          textAnchor="end"
                        >
                          {pitch}
                        </Text>
                      ))}
                    </Group>
                    <Group left={margin.left}>
                      <rect
                        x={-margin.left}
                        y={0}
                        width={svgWidth - margin.right}
                        height={topBarHeight}
                        fill={rangeActiveBackground}
                      />
                      {!loading &&
                        // A NaN/Infinity/unset (-1) duration yields a bogus marker count, painting dozens of time labels over a waveform that has not loaded yet
                        Number.isFinite(audioDuration) &&
                        audioDuration > 0 &&
                        Array.from(
                          {
                            length: Math.floor(
                              (audioDuration + 10) / xMarkerInterval
                            ),
                          },
                          (_, i) => (
                            <Text
                              key={i}
                              className="pointer-events-none cursor-pointer select-none"
                              x={secondsScale(i * xMarkerInterval)}
                              y={10}
                              fontSize={10}
                              textAnchor="middle"
                            >
                              {`${i * xMarkerInterval}s`}
                            </Text>
                          )
                        )}
                      {/** paint this back to the pitch label */}
                      <rect
                        x={0}
                        onClick={makeInactiveOnCondition(
                          loading,
                          isPlaying ?
                            (e) => {
                              const seekPosition = movePlayheadCommon(e);
                              isPausedRef.current = true;
                              audioRef.current?.pause();
                              setIsPlaying(false);
                              handlePlay(seekPosition);
                            }
                          : (e) => {
                              movePlayheadCommon(e);
                            }
                        )}
                        className="cursor-pointer"
                        y={0}
                        opacity={0}
                        width={svgInnerWidth}
                        height={topBarHeight}
                        fill={"#ffffff"}
                      />
                    </Group>
                  </svg>
                  {/* Opaque: in the terminal no-markers state the editor underneath is empty and must be neither visible nor clickable */}
                  {(loading || hasNoPitchMarkers) && (
                    <div
                      className="absolute left-0 top-0 bg-white"
                      style={{
                        width: svgWidth,
                        height: svgHeight,
                      }}
                    >
                      <div className="flex h-full w-full flex-col">
                        <div className="grow"></div>
                        <div className="flex flex-row justify-center">
                          {hasNoPitchMarkers ?
                            <span>
                              We could not find any pitch in this audio 😔
                            </span>
                          : loadingIndex === 0 || loadingIndex === 6 ?
                            <span className="animate-bounce">
                              Converting audio 🔊
                            </span>
                          : loadingIndex === 1 || loadingIndex === 7 ?
                            <span className="animate-bounce">
                              Analyzing frequencies 📊
                            </span>
                          : loadingIndex === 2 || loadingIndex === 8 ?
                            <span className="animate-bounce">
                              Finding nearest pitches 🎶
                            </span>
                          : loadingIndex === 3 || loadingIndex === 9 ?
                            <span className="animate-bounce">
                              Smoothing transients 🌊
                            </span>
                          : loadingIndex === 4 || loadingIndex === 10 ?
                            <span className="animate-bounce">
                              Removing low frequencies 🧹
                            </span>
                          : loadingIndex === 5 || loadingIndex === 11 ?
                            <span className="animate-bounce">
                              LFO on pitch changes 🚀
                            </span>
                          : <span>
                              Something went wrong, please try again 😔
                            </span>
                          }
                        </div>
                        <div className="grow"></div>
                      </div>
                    </div>
                  )}

                  {!sufficientlyZoomedToBeEditable &&
                    !loading &&
                    !hasNoPitchMarkers && (
                      <div className="absolute bottom-0 right-0 animate-pulse select-none p-1">
                        Zoom in to edit
                      </div>
                    )}
                </div>
              }
              <div
                className={classNames(
                  "px-2",
                  ...(introVideoIsShowing ? ["invisible"] : [])
                )}
              >
                <Range
                  step={1}
                  min={minY}
                  max={maxY}
                  disabled={loading || isPlaying}
                  direction={Direction.Up}
                  values={[Math.round(yZoom[0]), Math.round(yZoom[1])]}
                  onChange={makeInactiveOnCondition(
                    loading || isPlaying,
                    handleYZoomChange
                  )}
                  renderTrack={({ props, children }) => (
                    <div
                      {...props}
                      onMouseDown={makeInactiveOnCondition(
                        loading || isPlaying,
                        (event) => {
                          setYRangeElt(props.ref.current);
                          const rect =
                            event.currentTarget.getBoundingClientRect();
                          const y = (event.clientY - rect.top) / rect.height;
                          const yPos = scaleLinear({
                            domain: [0, 1],
                            range: [maxY, minY],
                          })(y);
                          if (inThumb.current) {
                            props.onMouseDown(event);
                            return;
                          }
                          setYRangeMouseDownStart({
                            pos: yPos,
                            zoom0: yZoom[0],
                            zoom1: yZoom[1],
                          });
                        }
                      )}
                      style={{
                        ...props.style,
                        width: "12px",
                        background:
                          !isPlaying ?
                            getTrackBackground({
                              min: minY || 0,
                              max: maxY || 100,
                              values: yZoom,
                              colors: [
                                rangeDisabledBackground,
                                rangeActiveBackground,
                                rangeDisabledBackground,
                              ],
                              direction: Direction.Up,
                            })
                          : rangeDisabledBackground,
                        height: "100%",
                      }}
                    >
                      {children}
                    </div>
                  )}
                  renderThumb={({ props }) => (
                    <div
                      {...props}
                      key={props.key}
                      onMouseDown={makeInactiveOnCondition(
                        loading || isPlaying,
                        (event) => {
                          inThumb.current = true;
                          event.preventDefault();
                        }
                      )}
                      style={{
                        ...props.style,
                        borderRadius: "50%",
                        height: "14px",
                        width: "14px",
                        background:
                          !isPlaying ?
                            handleActiveBackground
                          : handleDisabledBackground,
                      }}
                    />
                  )}
                />
              </div>
            </div>
            <div
              className={classNames(
                "mt-2 flex flex-col space-y-4 pr-3",
                ...(introVideoIsShowing ? ["invisible"] : [])
              )}
            >
              <Range
                step={0.01}
                min={0}
                max={1}
                disabled={isPlaying}
                values={[
                  Math.round(xZoom[0] * 100) / 100,
                  Math.round(xZoom[1] * 100) / 100,
                ]}
                onChange={makeInactiveOnCondition(
                  loading || isPlaying,
                  handleXZoomChange
                )}
                renderTrack={({ props, children }) => (
                  <div
                    {...props}
                    onMouseDown={makeInactiveOnCondition(
                      loading || isPlaying,
                      (event) => {
                        setXRangeElt(props.ref.current);
                        const rect =
                          event.currentTarget.getBoundingClientRect();
                        const x = (event.clientX - rect.left) / rect.width;
                        if (inThumb.current) {
                          props.onMouseDown(event);
                          return;
                        }
                        setXRangeMouseDownStart({
                          pos: x,
                          zoom0: xZoom[0],
                          zoom1: xZoom[1],
                        });
                      }
                    )}
                    style={{
                      ...props.style,
                      height: "12px",
                      background:
                        !isPlaying ?
                          getTrackBackground({
                            min: 0,
                            max: 1,
                            values: xZoom,
                            colors: [
                              rangeDisabledBackground,
                              rangeActiveBackground,
                              rangeDisabledBackground,
                            ],
                          })
                        : rangeDisabledBackground,
                      width: "100%",
                    }}
                  >
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div
                    {...props}
                    onMouseDown={makeInactiveOnCondition(
                      loading || isPlaying,
                      (event) => {
                        inThumb.current = true;
                        event.preventDefault();
                      }
                    )}
                    key={props.key}
                    style={{
                      ...props.style,
                      borderRadius: "50%",
                      height: "14px",
                      width: "14px",
                      background:
                        !isPlaying ?
                          handleActiveBackground
                        : handleDisabledBackground,
                    }}
                  />
                )}
              />
            </div>
            <div className="grid w-full grid-cols-[1fr_max-content_min-content] items-center gap-x-4 pt-2 md:grid-cols-[1fr_max-content_1fr]">
              <button
                onClick={() =>
                  setIntroVideoIsShowing(
                    (prevIntroVideoIsShowing) => !prevIntroVideoIsShowing
                  )
                }
                className="group flex w-fit items-center gap-x-4 rounded-lg bg-primaryLight px-4 py-2"
              >
                {introVideoIsShowing ?
                  <NormalText elementType="p" className="mx-auto max-w-xl">
                    Return to editor →
                  </NormalText>
                : <>
                    <Image
                      src={autotuneIntroductionVideoThumbnail}
                      alt={"Instruction video pitch editor"}
                      className="rounded-lg object-cover"
                      width={80}
                      height={80}
                      quality={100}
                    />

                    <NormalText elementType="p" className="mx-auto max-w-xl">
                      Watch intro video →
                    </NormalText>
                  </>
                }
              </button>
              <div
                className={classNames(
                  "flex",
                  "items-center",
                  "space-x-2",
                  "justify-self-center",
                  ...(introVideoIsShowing ? ["invisible"] : [])
                )}
              >
                <RadioGroup
                  value={mode}
                  onChange={makeInactiveOnCondition(
                    loading || isPlaying,
                    setMode
                  )}
                  className="relative grid grid-cols-4 gap-x-1 rounded-full p-1 text-center ring-1 ring-inset ring-gray-200"
                >
                  <RadioGroup.Label className="sr-only">
                    Payment frequency
                  </RadioGroup.Label>
                  <RadioGroup.Option
                    value={"pitch"}
                    onDragStart={(e) => e.preventDefault()}
                    className={({ checked }) =>
                      classNames(
                        checked ? "bg-primary text-white" : "text-gray-900",
                        "cursor-pointer select-none rounded-full px-2.5 py-1"
                      )
                    }
                  >
                    <SmallTitle elementType="span" className="text-inherit">
                      Pitch
                    </SmallTitle>
                  </RadioGroup.Option>
                  <RadioGroup.Option
                    value={"vibrato"}
                    onDragStart={(e) => e.preventDefault()}
                    className={({ checked }) =>
                      classNames(
                        checked ? "bg-primary text-white" : "text-gray-900",
                        "cursor-pointer select-none rounded-full px-2.5 py-1"
                      )
                    }
                  >
                    <SmallTitle elementType="span" className="text-inherit">
                      Vibrato
                    </SmallTitle>
                  </RadioGroup.Option>{" "}
                  <RadioGroup.Option
                    value={"drift"}
                    onDragStart={(e) => e.preventDefault()}
                    className={({ checked }) =>
                      classNames(
                        checked ? "bg-primary text-white" : "text-gray-900",
                        "cursor-pointer select-none rounded-full px-2.5 py-1"
                      )
                    }
                  >
                    <SmallTitle elementType="span" className="text-inherit">
                      Drift
                    </SmallTitle>
                  </RadioGroup.Option>
                  <RadioGroup.Option
                    value={"cut"}
                    onDragStart={(e) => e.preventDefault()}
                    className={({ checked }) =>
                      classNames(
                        checked ? "bg-primary text-white" : "text-gray-900",
                        "cursor-pointer select-none rounded-full px-2.5 py-1"
                      )
                    }
                  >
                    <SmallTitle elementType="span" className="text-inherit">
                      Cut
                    </SmallTitle>
                  </RadioGroup.Option>
                </RadioGroup>
              </div>

              {!introVideoIsShowing && (
                <Button
                  className="ml-auto w-fit"
                  loading={submitting}
                  disabled={loading || submitting}
                  onClick={() => callbackPitchesToModal(pitches, setSubmitting)}
                >
                  {actionName}
                </Button>
              )}
            </div>

            <Tooltip
              place={"bottom-end"}
              id="got-a-question?"
              noArrow={isFullScreen}
            >
              <div>
                <h3 className="py-2 text-lg font-semibold">Some tips</h3>
                <table className="table-auto">
                  <tbody>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        Zoom
                      </td>
                      <td className="border border-white p-2">{`Scroll or use the scroll bar to zoom.`}</td>
                    </tr>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        Pitch mode
                      </td>
                      <td className="border border-white p-2">
                        Drag pitch lines up or down to change the pitch.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        Drift mode
                      </td>
                      <td className="border border-white p-2">
                        Drag pitch lines up or down to center the pitch.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        Vibrato mode
                      </td>
                      <td className="border border-white p-2">
                        Drag pitch lines up or down to change the amount of
                        vibrato.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        Cut mode
                      </td>
                      <td className="border border-white p-2">
                        Create, delete, and move split points.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        <Image
                          src={knobsPic}
                          alt="De-vibrato and Pitch knobs"
                          style={{ height: 40, width: "fit-content" }}
                        />
                      </td>
                      <td className="border border-white p-2">
                        Use the &apos;De-vibrato&apos; knob to correct all
                        vibrato. Use the &apos;Pitch&apos; knob and key select
                        to tune all pitches.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        <span className="rounded bg-gray-500 px-2 py-1 uppercase">
                          Piano roll
                        </span>
                      </td>
                      <td className="border border-white p-2">
                        Double-click a piano roll key to snap all notes to it.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        <div className="flex flex-row">
                          <MinusCircleIcon
                            className={`mx-1 h-6 w-6 stroke-white`}
                          />
                          <PlusCircleIcon
                            className={`mx-1 h-6 w-6 stroke-white`}
                          />
                        </div>
                      </td>
                      <td className="border border-white p-2">
                        Perform a diatonic transposition down or up one step.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        <div className="flex flex-row">
                          <ArrowUturnLeftIcon
                            className={`mx-1 h-6 w-6 stroke-white`}
                          />
                          <ArrowUturnRightIcon
                            className={`mx-1 h-6 w-6 stroke-white`}
                          />
                          <XCircleIcon
                            className={`mx-1 h-6 w-6 stroke-white`}
                          />
                        </div>
                      </td>
                      <td className="border border-white p-2">
                        Undo, redo, and cancel all changes.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        <span className="rounded bg-gray-500 px-2 py-1 uppercase">
                          CTRL
                        </span>
                      </td>
                      <td className="border border-white p-2">
                        Hold to select multiple notes by dragging.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        <span className="rounded bg-gray-500 px-2 py-1 uppercase">
                          SHIFT
                        </span>
                      </td>
                      <td className="border border-white p-2">
                        Hold to select multiple notes by clicking.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-white p-2 font-semibold">
                        <span className="rounded bg-gray-500 px-2 py-1 uppercase">
                          OPTION
                        </span>
                        /
                        <span className="rounded bg-gray-500 px-2 py-1 uppercase">
                          ALT
                        </span>
                      </td>
                      <td className="border border-white p-2">
                        Hold in pitch mode for fine-grained shifting.
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="mt-2"></div>
              </div>
            </Tooltip>
          </div>
        </FullScreen>;
  },
  () => true
);

PitchCorrection.displayName = "PitchCorrection";

export default PitchCorrection;
